import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  Collapse,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import HomeIcon from '@mui/icons-material/Home';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BusinessIcon from '@mui/icons-material/Business';
import FolderIcon from '@mui/icons-material/Folder';
import SecurityIcon from '@mui/icons-material/Security';
import BugReportIcon from '@mui/icons-material/BugReport';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ForumIcon from '@mui/icons-material/Forum';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import DashboardIcon from '@mui/icons-material/Dashboard';
import RateReviewIcon from '@mui/icons-material/RateReview';
import HandymanIcon from '@mui/icons-material/Handyman';
import PeopleIcon from '@mui/icons-material/People';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SettingsIcon from '@mui/icons-material/Settings';
import EmailIcon from '@mui/icons-material/Email';
import BackupIcon from '@mui/icons-material/Backup';
import TableChartIcon from '@mui/icons-material/TableChart';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import TuneIcon from '@mui/icons-material/Tune';
import ExploreIcon from '@mui/icons-material/Explore';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import GroupsIcon from '@mui/icons-material/Groups';
import ScienceIcon from '@mui/icons-material/Science';
import NotesIcon from '@mui/icons-material/Notes';

/* ──────────────────────────────────────────────────────────────────── */
/*  Types                                                              */
/* ──────────────────────────────────────────────────────────────────── */

interface DocSection {
  id: string;
  label: string;
  icon: React.ReactElement;
  subsections?: { id: string; label: string }[];
}

interface DocGroup {
  id: string;
  label: string;
  sections: DocSection[];
}

/* ──────────────────────────────────────────────────────────────────── */
/*  Navigation structure (grouped like the app sidebar)                */
/* ──────────────────────────────────────────────────────────────────── */

const groups: DocGroup[] = [
  {
    id: 'einstieg',
    label: 'Einstieg',
    sections: [
      { id: 'willkommen', label: 'Willkommen', icon: <HomeIcon fontSize="small" /> },
      {
        id: 'grundkonzepte',
        label: 'Grundkonzepte',
        icon: <AccountTreeIcon fontSize="small" />,
        subsections: [
          { id: 'grundkonzepte-hierarchie', label: 'Entitäts-Hierarchie' },
          { id: 'grundkonzepte-beziehungen', label: 'Beziehungsdiagramm' },
          { id: 'grundkonzepte-weitere', label: 'Weitere Bereiche' },
        ],
      },
      {
        id: 'bedienung',
        label: 'Navigation & Bedienung',
        icon: <ExploreIcon fontSize="small" />,
        subsections: [
          { id: 'bedienung-navigation', label: 'Menüstruktur' },
          { id: 'bedienung-suche', label: 'Globale Suche' },
          { id: 'bedienung-benachrichtigungen', label: 'Benachrichtigungen' },
          { id: 'bedienung-darstellung', label: 'Darstellung & Profilmenü' },
        ],
      },
    ],
  },
  {
    id: 'mein-bereich',
    label: 'Mein Bereich',
    sections: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: <DashboardIcon fontSize="small" />,
        subsections: [
          { id: 'dashboard-widgets', label: 'Widgets' },
          { id: 'dashboard-profile', label: 'Profile & Bearbeiten' },
        ],
      },
      { id: 'zeiterfassung', label: 'Zeiterfassung', icon: <AccessTimeIcon fontSize="small" /> },
      { id: 'eingangsbelege', label: 'Eingangsbelege', icon: <MoveToInboxIcon fontSize="small" /> },
      {
        id: 'urlaub',
        label: 'Abwesenheiten',
        icon: <BeachAccessIcon fontSize="small" />,
        subsections: [
          { id: 'abwesenheiten-urlaub', label: 'Urlaub' },
          { id: 'abwesenheiten-krankheit', label: 'Krankheit' },
          { id: 'abwesenheiten-workation', label: 'Workation' },
        ],
      },
      { id: 'anwesenheit', label: 'Anwesenheit', icon: <GroupsIcon fontSize="small" /> },
      { id: 'hall-of-fame', label: 'Hall of Fame', icon: <EmojiEventsIcon fontSize="small" /> },
      { id: 'isolierte-umgebung', label: 'Isolierte Umgebung', icon: <ScienceIcon fontSize="small" /> },
      {
        id: 'diskussion',
        label: 'Interne Diskussion',
        icon: <ForumIcon fontSize="small" />,
        subsections: [
          { id: 'diskussion-uebersicht', label: 'Überblick' },
          { id: 'diskussion-funktionen', label: 'Beiträge & Funktionen' },
          { id: 'diskussion-anker', label: 'Anker & Erwähnungen' },
        ],
      },
      {
        id: 'profil',
        label: 'Profil & Sicherheit',
        icon: <PersonIcon fontSize="small" />,
        subsections: [
          { id: 'profil-daten', label: 'Profil & Sichtbarkeit' },
          { id: 'profil-sicherheit', label: 'Passwort & 2FA' },
          { id: 'profil-benachrichtigungen', label: 'Benachrichtigungseinstellungen' },
        ],
      },
    ],
  },
  {
    id: 'projekte-audits',
    label: 'Projekte & Audits',
    sections: [
      {
        id: 'projekte',
        label: 'Projekte',
        icon: <FolderIcon fontSize="small" />,
        subsections: [
          { id: 'projekte-typen', label: 'Projekttypen' },
          { id: 'projekte-team', label: 'Projektteam' },
          { id: 'projekte-status', label: 'Status' },
        ],
      },
      { id: 'kanban', label: 'Kanban Board', icon: <ViewColumnIcon fontSize="small" /> },
      { id: 'auditkalender', label: 'Auditkalender', icon: <CalendarMonthIcon fontSize="small" /> },
      {
        id: 'audits',
        label: 'Audits',
        icon: <SecurityIcon fontSize="small" />,
        subsections: [
          { id: 'audits-typen', label: 'Audit-Typen' },
          { id: 'audits-status', label: 'Status' },
          { id: 'audits-zeitraeume', label: 'Durchführungszeiträume' },
          { id: 'audits-team', label: 'Audit-Team' },
          { id: 'audits-bericht', label: 'Bericht & Demo-Modus' },
          { id: 'audits-firewall', label: 'Firewall-Audit' },
          { id: 'audits-historie', label: 'Versions- & Änderungshistorie' },
        ],
      },
      {
        id: 'schwachstellen',
        label: 'Schwachstellen',
        icon: <BugReportIcon fontSize="small" />,
        subsections: [
          { id: 'schwachstellen-kritikalitaet', label: 'Kritikalitätsstufen' },
          { id: 'schwachstellen-klassen', label: 'Schwachstellenklassen' },
          { id: 'schwachstellen-review', label: 'Status-Workflow & 4-Augen-Review' },
          { id: 'schwachstellen-tools', label: 'Tools, CWE & Nachweise' },
        ],
      },
      { id: 'reviews', label: 'Reviews', icon: <RateReviewIcon fontSize="small" /> },
    ],
  },
  {
    id: 'workbench',
    label: 'Workbench (Toolbox)',
    sections: [
      {
        id: 'workbench',
        label: 'Toolbox',
        icon: <HandymanIcon fontSize="small" />,
        subsections: [
          { id: 'workbench-uebersicht', label: 'Übersicht & Laufzeitmodell' },
          { id: 'workbench-scanning', label: 'Scanning & Recon' },
          { id: 'workbench-utilities', label: 'Utilities' },
          { id: 'workbench-pta', label: 'PTA – Transkription' },
          { id: 'workbench-drawio', label: 'Draw.io Editor' },
        ],
      },
    ],
  },
  {
    id: 'verwaltung',
    label: 'Verwaltung',
    sections: [
      {
        id: 'kunden',
        label: 'Kunden & Ansprechpartner',
        icon: <BusinessIcon fontSize="small" />,
        subsections: [
          { id: 'kunden-uebersicht', label: 'Übersicht' },
          { id: 'kunden-ansprechpartner', label: 'Ansprechpartner' },
        ],
      },
      {
        id: 'angebote',
        label: 'Angebote',
        icon: <RequestQuoteIcon fontSize="small" />,
        subsections: [
          { id: 'angebote-status', label: 'Angebotsstatus' },
          { id: 'angebote-projekt', label: 'Projekt erzeugen' },
        ],
      },
      {
        id: 'rechnungen',
        label: 'Rechnungen',
        icon: <ReceiptIcon fontSize="small" />,
        subsections: [
          { id: 'rechnungen-positionen', label: 'Rechnungspositionen' },
          { id: 'rechnungen-status', label: 'Rechnungsstatus' },
          { id: 'rechnungen-e-rechnung', label: 'E-Rechnung & Aufteilung' },
        ],
      },
      { id: 'rechnungsvorlagen', label: 'Rechnungsvorlagen', icon: <NotesIcon fontSize="small" /> },
      { id: 'mitarbeiter', label: 'Mitarbeiterverwaltung', icon: <PeopleIcon fontSize="small" /> },
      { id: 'urlaub-uebersicht', label: 'Abwesenheitsübersicht', icon: <EventAvailableIcon fontSize="small" /> },
      { id: 'stammdaten', label: 'Stammdaten', icon: <SettingsIcon fontSize="small" /> },
      {
        id: 'daten-transfer',
        label: 'Datenimport & -export',
        icon: <ImportExportIcon fontSize="small" />,
        subsections: [{ id: 'daten-transfer-ablauf', label: 'Ablauf & Hinweise' }],
      },
      { id: 'kundenuploads', label: 'Kundenuploads', icon: <CloudUploadIcon fontSize="small" /> },
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    sections: [
      { id: 'systemkonfiguration', label: 'Systemkonfiguration', icon: <TuneIcon fontSize="small" /> },
      { id: 'email-templates', label: 'E-Mail-Templates', icon: <EmailIcon fontSize="small" /> },
      { id: 'backups', label: 'Backups', icon: <BackupIcon fontSize="small" /> },
      { id: 'berechtigungsmatrix', label: 'Berechtigungsmatrix', icon: <TableChartIcon fontSize="small" /> },
      { id: 'debug-wartung', label: 'Debug & Wartung', icon: <BugReportIcon fontSize="small" /> },
    ],
  },
  {
    id: 'referenz',
    label: 'Referenz',
    sections: [
      {
        id: 'rollen',
        label: 'Rollen & Berechtigungen',
        icon: <AdminPanelSettingsIcon fontSize="small" />,
        subsections: [
          { id: 'rollen-uebersicht', label: 'Rollenübersicht' },
          { id: 'rollen-berechtigungen', label: 'Berechtigungsmatrix' },
        ],
      },
    ],
  },
];

const allSections: DocSection[] = groups.flatMap((g) => g.sections);

/* ──────────────────────────────────────────────────────────────────── */
/*  Helper: styled components for docs                                 */
/* ──────────────────────────────────────────────────────────────────── */

const SectionTitle: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <Typography
    id={id}
    variant="h4"
    sx={{ fontWeight: 700, mt: 5, mb: 2, scrollMarginTop: '24px' }}
  >
    {children}
  </Typography>
);

const SubTitle: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <Typography
    id={id}
    variant="h5"
    sx={{ fontWeight: 600, mt: 4, mb: 1.5, scrollMarginTop: '24px' }}
  >
    {children}
  </Typography>
);

const Paragraph: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, color: 'text.secondary' }}>
    {children}
  </Typography>
);

const InlineCode: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  return (
    <Box
      component="span"
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.85em',
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        px: 0.8,
        py: 0.2,
        borderRadius: 0.5,
      }}
    >
      {children}
    </Box>
  );
};

const DocAlert: React.FC<{ severity?: 'info' | 'warning' | 'success'; children: React.ReactNode }> = ({
  severity = 'info',
  children,
}) => (
  <Alert severity={severity} sx={{ mb: 2 }}>
    {children}
  </Alert>
);

/* ──────────────────────────────────────────────────────────────────── */
/*  Proximity-highlight: MenuLink + DocLink                            */
/* ──────────────────────────────────────────────────────────────────── */

const menuLinkRegistry = new Map<HTMLElement, string>();
const PROXIMITY_RADIUS = 50;

const MenuLink: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const el = ref.current;
    if (el) {
      menuLinkRegistry.set(el, to);
      return () => { menuLinkRegistry.delete(el); };
    }
  }, [to]);

  return (
    <Box
      component="span"
      ref={ref}
      onClick={() => navigate(to)}
      sx={{
        color: theme.palette.primary.main,
        fontWeight: 600,
        cursor: 'pointer',
        borderBottom: `1px dashed ${theme.palette.primary.main}55`,
        transition: 'border-color 0.15s, color 0.15s',
        '&:hover': {
          borderBottomStyle: 'solid',
          borderBottomColor: theme.palette.primary.main,
        },
      }}
    >
      {children}
    </Box>
  );
};

const DocLink: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => {
  const theme = useTheme();

  const handleClick = () => {
    const el = document.getElementById(to);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box
      component="span"
      onClick={handleClick}
      sx={{
        color: theme.palette.primary.main,
        fontWeight: 500,
        cursor: 'pointer',
        borderBottom: `1px dashed ${theme.palette.primary.main}40`,
        transition: 'border-color 0.15s',
        '&:hover': {
          borderBottomStyle: 'solid',
          borderBottomColor: theme.palette.primary.main,
        },
      }}
    >
      {children}
    </Box>
  );
};

/* ──────────────────────────────────────────────────────────────────── */
/*  Relationship Diagram Component                                     */
/* ──────────────────────────────────────────────────────────────────── */

const RelationshipDiagram: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const nodeStyle = (color: string) => ({
    px: 2,
    py: 1,
    borderRadius: 2,
    border: `2px solid ${color}`,
    bgcolor: isDark ? `${color}22` : `${color}11`,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    fontWeight: 600,
    fontSize: '0.85rem',
    color: isDark ? '#e0e0e0' : '#333',
    whiteSpace: 'nowrap',
  });

  const connectorStyle = {
    borderLeft: `2px solid ${theme.palette.divider}`,
    ml: 2.5,
    pl: 2.5,
    py: 0.5,
  };

  return (
    <Paper
      variant="outlined"
      sx={{ p: 3, mb: 3, overflow: 'auto' }}
    >
      <Box sx={nodeStyle('#1976d2')}>
        <BusinessIcon sx={{ fontSize: 18 }} /> Kunde
      </Box>
      <Box sx={connectorStyle}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={nodeStyle('#66bb6a')}>
            <RequestQuoteIcon sx={{ fontSize: 18 }} /> Angebot
            <Chip label="Entwurf → Gewonnen" size="small" variant="outlined" sx={{ ml: 0.5 }} />
          </Box>
          <Box sx={nodeStyle('#2e7d32')}>
            <FolderIcon sx={{ fontSize: 18 }} /> Projekt
            <Chip label="Single / Kontingent / Festpreis" size="small" variant="outlined" sx={{ ml: 0.5 }} />
          </Box>
          <Box sx={connectorStyle}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={nodeStyle('#ed6c02')}>
                <SecurityIcon sx={{ fontSize: 18 }} /> Audit
                <Chip label="8 Typen" size="small" variant="outlined" sx={{ ml: 0.5 }} />
                <Chip label="7 Status" size="small" variant="outlined" />
              </Box>
              <Box sx={connectorStyle}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={nodeStyle('#d32f2f')}>
                    <BugReportIcon sx={{ fontSize: 18 }} /> Schwachstelle
                    <Chip label="5 Kritikalitätsstufen" size="small" variant="outlined" sx={{ ml: 0.5 }} />
                  </Box>
                  <Box sx={nodeStyle('#7b1fa2')}>
                    <CalendarMonthIcon sx={{ fontSize: 18 }} /> Durchführungszeitraum
                  </Box>
                </Box>
              </Box>
              <Box sx={nodeStyle('#0288d1')}>
                <AccessTimeIcon sx={{ fontSize: 18 }} /> Zeiteintrag
              </Box>
              <Box sx={nodeStyle('#558b2f')}>
                <ReceiptIcon sx={{ fontSize: 18 }} /> Rechnung
              </Box>
              <Box sx={{ ...connectorStyle, borderLeft: `2px solid ${theme.palette.divider}`, ml: 2.5, pl: 2.5 }}>
                <Box sx={nodeStyle('#6d4c41')}>
                  Rechnungsposition
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

/* ──────────────────────────────────────────────────────────────────── */
/*  Main DocsPage Component                                            */
/* ──────────────────────────────────────────────────────────────────── */

const NAV_WIDTH = 280;

const DocsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeId, setActiveId] = useState('willkommen');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const contentRef = useRef<HTMLDivElement>(null);

  const accent = theme.palette.primary.main;
  const navBg = isDark ? '#1a1e36' : '#f8f9fb';
  const navBorder = theme.palette.divider;
  const navText = isDark ? '#b0bec5' : '#3a4a5a';
  const navTextActive = accent;

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const scrollTo = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setActiveId(id);
    if (isMobile) setMobileNavOpen(false);
  }, [isMobile]);

  useEffect(() => {
    const allIds = allSections.flatMap((s) => [s.id, ...(s.subsections?.map((sub) => sub.id) || [])]);
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
            break;
          }
        }
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0.1 }
    );

    allIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let rafId = 0;
    const handler = (e: MouseEvent) => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        let bestPath: string | null = null;
        let bestOpacity = 0;

        menuLinkRegistry.forEach((path, el) => {
          const rect = el.getBoundingClientRect();
          const dx = Math.max(0, Math.abs(e.clientX - (rect.left + rect.width / 2)) - rect.width / 2);
          const dy = Math.max(0, Math.abs(e.clientY - (rect.top + rect.height / 2)) - rect.height / 2);
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < PROXIMITY_RADIUS) {
            const opacity = Math.pow(1 - dist / PROXIMITY_RADIUS, 1.5);
            if (opacity > bestOpacity) {
              bestOpacity = opacity;
              bestPath = path;
            }
          }
        });

        document.dispatchEvent(
          new CustomEvent('docs-sidebar-highlight', {
            detail: { path: bestPath, opacity: bestOpacity },
          }),
        );
      });
    };

    document.addEventListener('mousemove', handler, { passive: true });
    return () => {
      document.removeEventListener('mousemove', handler);
      cancelAnimationFrame(rafId);
      document.dispatchEvent(
        new CustomEvent('docs-sidebar-highlight', {
          detail: { path: null, opacity: 0 },
        }),
      );
    };
  }, []);

  /* ── Navigation sidebar ── */
  const navContent = (
    <Box
      sx={{
        width: NAV_WIDTH,
        height: '100%',
        overflow: 'auto',
        bgcolor: navBg,
        borderRight: `1px solid ${navBorder}`,
        py: 2,
      }}
    >
      <Box sx={{ px: 2.5, pb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <MenuBookIcon sx={{ color: accent, fontSize: 22 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Dokumentation
        </Typography>
      </Box>
      <Divider sx={{ mb: 1 }} />
      <List disablePadding dense>
        {groups.map((group) => (
          <React.Fragment key={group.id}>
            <Typography
              sx={{
                px: 2.5,
                pt: 2,
                pb: 0.5,
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: navText,
                opacity: 0.7,
              }}
            >
              {group.label}
            </Typography>
            {group.sections.map((section) => {
              const isActive = activeId === section.id || section.subsections?.some((s) => s.id === activeId);
              const hasSubsections = section.subsections && section.subsections.length > 0;
              const isExpanded = expandedSections[section.id] ?? isActive;

              return (
                <React.Fragment key={section.id}>
                  <ListItemButton
                    onClick={() => {
                      scrollTo(section.id);
                      if (hasSubsections) toggleSection(section.id);
                    }}
                    sx={{
                      py: 0.8,
                      px: 2.5,
                      borderLeft: isActive ? `3px solid ${accent}` : '3px solid transparent',
                      '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, color: isActive ? navTextActive : navText }}>
                      {section.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={section.label}
                      primaryTypographyProps={{
                        fontSize: '0.82rem',
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? navTextActive : navText,
                      }}
                    />
                    {hasSubsections && (isExpanded ? <ExpandLess sx={{ fontSize: 18, color: navText }} /> : <ExpandMore sx={{ fontSize: 18, color: navText }} />)}
                  </ListItemButton>
                  {hasSubsections && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List disablePadding dense>
                        {section.subsections!.map((sub) => (
                          <ListItemButton
                            key={sub.id}
                            onClick={() => scrollTo(sub.id)}
                            sx={{
                              py: 0.5,
                              pl: 5.5,
                              pr: 2.5,
                              '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 24, color: activeId === sub.id ? navTextActive : navText }}>
                              <ArrowRightIcon sx={{ fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={sub.label}
                              primaryTypographyProps={{
                                fontSize: '0.78rem',
                                fontWeight: activeId === sub.id ? 600 : 400,
                                color: activeId === sub.id ? navTextActive : navText,
                              }}
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  /* ── Main content ── */
  return (
    <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 100px)', mx: -3, mt: -3 }}>
      {/* Mobile nav toggle */}
      {isMobile && (
        <IconButton
          onClick={() => setMobileNavOpen(true)}
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1200, bgcolor: accent, color: '#fff', '&:hover': { bgcolor: accent } }}
        >
          <MenuBookIcon />
        </IconButton>
      )}

      {/* Navigation */}
      {isMobile ? (
        <Drawer
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: NAV_WIDTH } }}
        >
          {navContent}
        </Drawer>
      ) : (
        <Box sx={{ width: NAV_WIDTH, flexShrink: 0, position: 'sticky', top: 0, height: 'calc(100vh - 64px)', alignSelf: 'flex-start' }}>
          {navContent}
        </Box>
      )}

      {/* Content area */}
      <Box
        ref={contentRef}
        sx={{
          flexGrow: 1,
          maxWidth: 900,
          px: { xs: 2, sm: 4, md: 5 },
          py: 4,
          mx: 'auto',
        }}
      >
        {/* ──────────────────────────────────────── */}
        {/*  1. WILLKOMMEN                           */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="willkommen">Willkommen bei STARK-AIO</SectionTitle>
        <Paragraph>
          <strong>STARK-AIO</strong> ist die zentrale Plattform der STARK IT-Security GmbH für die Verwaltung von
          IT-Security-Audits, Projekten und allen damit verbundenen Prozessen. Die Anwendung deckt den gesamten Lebenszyklus
          ab – von der Kundenanlage über die Projektplanung und Audit-Durchführung bis hin zur Rechnungsstellung – und
          ergänzt diesen Kern um Werkzeuge für den Arbeitsalltag (<DocLink to="dashboard">Dashboard</DocLink>,{' '}
          <DocLink to="workbench">Pentest-Toolbox</DocLink>, <DocLink to="diskussion">interne Diskussion</DocLink>).
        </Paragraph>
        <Paragraph>
          Diese Dokumentation gibt Ihnen einen vollständigen Überblick über alle Module. Das Inhaltsverzeichnis links ist
          – wie das Hauptmenü der Anwendung – in Bereiche gegliedert: <strong>Mein Bereich</strong>,{' '}
          <strong>Projekte &amp; Audits</strong>, <strong>Workbench</strong>, <strong>Verwaltung</strong> und{' '}
          <strong>Administration</strong>. Nutzen Sie es, um direkt zum gewünschten Thema zu springen.
        </Paragraph>
        <DocAlert severity="info">
          Welche Menüpunkte und Funktionen Sie tatsächlich sehen, hängt von Ihren <strong>Rollen</strong> ab. Diese
          Dokumentation beschreibt den vollen Funktionsumfang; Bereiche ohne passende Berechtigung sind in Ihrer Ansicht
          ausgeblendet. Der Abschnitt <DocLink to="rollen">Rollen &amp; Berechtigungen</DocLink> erklärt die Details.
        </DocAlert>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  2. GRUNDKONZEPTE                        */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="grundkonzepte">Grundkonzepte</SectionTitle>
        <Paragraph>
          STARK-AIO organisiert Daten in einer klaren Hierarchie. Das Verständnis dieser Struktur ist der Schlüssel
          zur effektiven Nutzung der Anwendung.
        </Paragraph>

        <SubTitle id="grundkonzepte-hierarchie">Entitäts-Hierarchie</SubTitle>
        <Paragraph>
          Alles beginnt beim <DocLink to="kunden">Kunden</DocLink>. Daraus entstehen <DocLink to="angebote">Angebote</DocLink>
          (Nachfrage und kaufmännisches Angebot) und – nach Beauftragung oder unabhängig davon – ein oder mehrere{' '}
          <DocLink to="projekte">Projekte</DocLink>.
          Jedes Projekt kann beliebig viele <DocLink to="audits">Audits</DocLink> enthalten. Innerhalb eines Audits werden
          die gefundenen <DocLink to="schwachstellen">Schwachstellen</DocLink> dokumentiert. Parallel dazu werden <DocLink to="zeiterfassung">Zeiteinträge</DocLink> erfasst
          und <DocLink to="rechnungen">Rechnungen</DocLink> erstellt (mit optionaler Angebotsnummer). Ergänzend – organisatorisch daneben, nicht „unter“ dem Kundenbaum – gibt es die{' '}
          <DocLink to="diskussion">interne Diskussion</DocLink> sowie in der <DocLink to="daten-transfer">Verwaltung</DocLink> den fachlichen Datenimport und -export.
        </Paragraph>

        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre', lineHeight: 2, color: 'text.secondary' }}>
{`Kunde
 ├── Angebot (Entwurf → Gesendet → Gewonnen / Verloren / Zurückgezogen)
 │     └── Projekt (erzeugen, nicht automatisch)
 └── Projekt (Typ: Single, Kontingent oder Festpreis)
       ├── Audit (8 Typen, 7 Status-Stufen)
       │     ├── Schwachstelle (5 Kritikalitätsstufen)
       │     │     └── Screenshots / Dateien
       │     └── Durchführungszeitraum
       ├── Zeiteintrag
       └── Rechnung
             └── Rechnungsposition (optional: Angebotsnummer)

Interne Diskussion (parallel, nicht Teil der Kunden-Hierarchie)
 └── Kategorie → Thread → Beiträge (Rich-Text, @Erwähnungen, Checklisten)
       └── optional: Anker → z. B. Projekt, Schwachstelle`}
          </Typography>
        </Paper>

        <DocAlert severity="success">
          <strong>Wichtig:</strong> Schwachstellen existieren immer im Kontext eines Audits, und Audits gehören immer zu einem Projekt.
          Wird ein Projekt gelöscht, sind alle darunter hängenden Daten betroffen.
        </DocAlert>

        <SubTitle id="grundkonzepte-beziehungen">Beziehungsdiagramm</SubTitle>
        <Paragraph>
          Das folgende Diagramm zeigt die Beziehungen der Kern-Entitäten visuell:
        </Paragraph>
        <RelationshipDiagram />

        <SubTitle id="grundkonzepte-weitere">Weitere Bereiche</SubTitle>
        <Paragraph>
          Das <strong>Kernmodell</strong> (Kunde → Angebot → Projekt → Audit → Schwachstelle) steht im Mittelpunkt des Diagramms oben. Zusätzlich
          pflegt STARK-AIO viele weitere fachliche Bereiche, etwa Rechnungswesen, <DocLink to="urlaub">Abwesenheiten</DocLink>, <DocLink to="daten-transfer">kontrollierten
          Import und Export fachlicher Daten</DocLink> und die <DocLink to="diskussion">interne Diskussion</DocLink> mit Kategorien, Threads und
          optionalen Verweisen (Ankern) auf Fachobjekte. Details zu Berechtigungen: <DocLink to="rollen">Rollen &amp; Berechtigungen</DocLink>.
        </Paragraph>

        <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1.5 }}>
          Die Rolle der Mitarbeiter
        </Typography>
        <Paragraph>
          <strong>Mitarbeiter</strong> sind das verbindende Element quer durch die gesamte Hierarchie. Ein und dieselbe
          Person kann in verschiedenen Kontexten unterschiedliche Aufgaben übernehmen:
        </Paragraph>
        <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1, color: 'text.secondary' } }}>
          <li>Auf <strong>Projektebene</strong> wird ein Mitarbeiter als <strong>Projektleiter</strong> benannt, der die Gesamtverantwortung trägt. Weitere Mitarbeiter bilden das <strong>Projektteam</strong>.</li>
          <li>Auf <strong>Auditebene</strong> wird ein eigenes <strong>Audit-Team</strong> zusammengestellt – das kann, muss aber nicht deckungsgleich mit dem Projektteam sein. So lassen sich bei <DocLink to="projekte-typen">Kontingent-Projekten</DocLink> mit vielen Audits unterschiedliche Besetzungen abbilden.</li>
          <li>Auf <strong>Schwachstellenebene</strong> gibt es zwei Rollen: Der <strong>Reporter</strong> erfasst den Fund und reicht ihn zur Prüfung ein, ein anderer Mitarbeiter prüft ihn als <strong>Reviewer</strong> (<DocLink to="schwachstellen-review">Status-Workflow & 4-Augen-Prinzip</DocLink>).</li>
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1.5 }}>
          Screenshots & Nachweise
        </Typography>
        <Paragraph>
          Zu jeder <strong>Schwachstelle</strong> können Screenshots und Dateien hochgeladen werden, die den Fund belegen.
          Die Uploads werden direkt in der Schwachstellen-Detailansicht verwaltet und sind damit immer im
          richtigen Kontext abrufbar – etwa beim Erstellen des Audit-Berichts oder während der Prüfung.
        </Paragraph>

        <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1.5 }}>
          Zeiteinträge
        </Typography>
        <Paragraph>
          <strong>Zeiteinträge</strong> erfassen die geleistete Arbeit pro Mitarbeiter. Jeder Eintrag ist einem Projekt zugeordnet
          und kann zusätzlich auf einen bestimmten Audit innerhalb des Projekts eingegrenzt werden. Über diese Zuordnung lässt
          sich später nachvollziehen, wie viel Aufwand in welchen Audit geflossen ist – und die Einträge können bei der
          <DocLink to="rechnungen">Rechnungsstellung</DocLink> direkt referenziert werden.
        </Paragraph>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  NAVIGATION & BEDIENUNG                  */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="bedienung">Navigation &amp; Bedienung</SectionTitle>
        <Paragraph>
          Dieser Abschnitt erklärt die wiederkehrenden Bedienelemente, die in der gesamten Anwendung verfügbar sind:
          das Hauptmenü, die globale Suche, Benachrichtigungen und die Darstellungsoptionen.
        </Paragraph>

        <SubTitle id="bedienung-navigation">Menüstruktur</SubTitle>
        <Paragraph>
          Die linke Seitenleiste ist in Bereiche gegliedert, die jeweils ein- und ausgeklappt werden können. Welche
          Bereiche und Einträge erscheinen, hängt von Ihren <DocLink to="rollen">Berechtigungen</DocLink> ab:
        </Paragraph>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 0.8, color: 'text.secondary' } }}>
          <li><strong>Mein Bereich:</strong> <DocLink to="dashboard">Dashboard</DocLink>, <DocLink to="zeiterfassung">Zeiterfassung</DocLink>, <DocLink to="eingangsbelege">Eingangsbelege</DocLink>, <DocLink to="urlaub">Abwesenheiten</DocLink>, <DocLink to="anwesenheit">Anwesenheit</DocLink>, <DocLink to="hall-of-fame">Hall of Fame</DocLink>, <DocLink to="isolierte-umgebung">Isolierte Umgebung</DocLink> und <DocLink to="diskussion">Diskussion</DocLink>.</li>
          <li><strong>Projekte &amp; Audits:</strong> <DocLink to="projekte">Projekte</DocLink>, <DocLink to="kanban">Kanban</DocLink>, <DocLink to="auditkalender">Auditkalender</DocLink>, <DocLink to="audits">Audits</DocLink> und (für Reviewer) die <DocLink to="reviews">Review-Queue</DocLink>.</li>
          <li><strong>Workbench:</strong> die <DocLink to="workbench">Pentest-Toolbox</DocLink>.</li>
          <li><strong>Verwaltung:</strong> <DocLink to="angebote">Angebote</DocLink>, <DocLink to="rechnungen">Rechnungen</DocLink>, <DocLink to="rechnungsvorlagen">Rechnungsvorlagen</DocLink>, <DocLink to="kunden">Kunden</DocLink>, <DocLink to="mitarbeiter">Mitarbeiter</DocLink>, <DocLink to="urlaub-uebersicht">Abwesenheitsübersicht</DocLink>, <DocLink to="stammdaten">Stammdaten</DocLink>, <DocLink to="daten-transfer">Datenimport/-export</DocLink> und <DocLink to="kundenuploads">Kundenuploads</DocLink>.</li>
          <li><strong>Admin:</strong> <DocLink to="systemkonfiguration">Systemkonfiguration</DocLink>, <DocLink to="email-templates">E-Mail-Templates</DocLink>, <DocLink to="backups">Backups</DocLink>, <DocLink to="berechtigungsmatrix">Berechtigungen</DocLink> und <DocLink to="debug-wartung">Debug &amp; Wartung</DocLink>.</li>
        </Box>
        <Paragraph>
          Über das STARK-Logo gelangen Sie jederzeit zurück zum Dashboard. Menüeinträge lassen sich per Mittelklick oder{' '}
          <InlineCode>Strg/Cmd</InlineCode>-Klick in einem neuen Tab öffnen.
        </Paragraph>

        <SubTitle id="bedienung-suche">Globale Suche</SubTitle>
        <Paragraph>
          Über das Suchfeld in der oberen Leiste – oder per Tastenkürzel <InlineCode>Strg/Cmd + K</InlineCode> – durchsuchen
          Sie zentral den gesamten Datenbestand. Die Suche erfasst <strong>Projekte</strong>, <strong>Kunden</strong>,{' '}
          <strong>Mitarbeiter</strong>, <strong>Ansprechpartner</strong>, <strong>Audits</strong>,{' '}
          <strong>Schwachstellen</strong>, <strong>Angebote</strong> und – sofern berechtigt – die <DocLink to="diskussion">interne Diskussion</DocLink>.
          Ein Klick auf ein Ergebnis springt direkt zum jeweiligen Datensatz.
        </Paragraph>
        <DocAlert severity="info">
          Die Suche steht nur Rollen mit der Berechtigung <strong>Suche verwenden</strong> zur Verfügung. Diskussionsinhalte
          erscheinen nur, wenn Sie zusätzlich Lesezugriff auf die Diskussion haben.
        </DocAlert>

        <SubTitle id="bedienung-benachrichtigungen">Benachrichtigungen</SubTitle>
        <Paragraph>
          Das <strong>Glocken-Symbol</strong> in der oberen Leiste zeigt Ihre persönlichen In-App-Benachrichtigungen
          (z. B. neue Review-Anfragen, @Erwähnungen oder Statusänderungen). Wichtige Meldungen werden hervorgehoben.
          STARK-AIO kann Sie zusätzlich per <strong>E-Mail</strong> und <strong>Push</strong> informieren – welche Ereignisse
          über welchen Kanal zugestellt werden, legen Sie in Ihren{' '}
          <DocLink to="profil-benachrichtigungen">Benachrichtigungseinstellungen</DocLink> fest.
        </Paragraph>

        <SubTitle id="bedienung-darstellung">Darstellung &amp; Profilmenü</SubTitle>
        <Paragraph>
          Über die obere Leiste schalten Sie zwischen <strong>Hell-</strong> und <strong>Dunkelmodus</strong> um. Das
          Benutzermenü (oben rechts) führt zu Ihrem <DocLink to="profil">Profil</DocLink> und zum Abmelden. Ganz unten in der
          Seitenleiste finden Sie den Link zu dieser <strong>Dokumentation</strong> sowie die installierte Versionsnummer.
        </Paragraph>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  DASHBOARD                               */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="dashboard">Dashboard</SectionTitle>
        <Paragraph>
          Das Dashboard ist Ihre persönliche Startseite (über das Logo oder den ersten Menüpunkt erreichbar). Es begrüßt
          Sie mit Namen und Datum und zeigt ein frei zusammenstellbares Raster aus <strong>Widgets</strong>. So sehen Sie
          auf einen Blick genau die Informationen, die für Ihre Arbeit relevant sind.
        </Paragraph>

        <SubTitle id="dashboard-widgets">Widgets</SubTitle>
        <Paragraph>
          Widgets sind kompakte Kacheln, die jeweils einen Bereich zusammenfassen. Verfügbar sind unter anderem:
        </Paragraph>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Widget</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Inhalt</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow><TableCell>Benachrichtigungen</TableCell><TableCell>Ihre aktuellen In-App-Meldungen</TableCell></TableRow>
              <TableRow><TableCell>Mein Urlaubsstand</TableCell><TableCell>Anspruch, Verbrauch und Resttage</TableCell></TableRow>
              <TableRow><TableCell>Meine Stunden</TableCell><TableCell>Zusammenfassung Ihrer erfassten Zeiten</TableCell></TableRow>
              <TableRow><TableCell>Workbench-Favoriten</TableCell><TableCell>Schnellzugriff auf markierte <DocLink to="workbench">Tools</DocLink></TableCell></TableRow>
              <TableRow><TableCell>Review-Queue</TableCell><TableCell>Offene <DocLink to="reviews">Reviews</DocLink> (für Reviewer)</TableCell></TableRow>
              <TableRow><TableCell>Heute</TableCell><TableCell>Heutige Termine und Durchführungen</TableCell></TableRow>
              <TableRow><TableCell>Anstehende Termine</TableCell><TableCell>Bevorstehende <DocLink to="audits-zeitraeume">Durchführungszeiträume</DocLink></TableCell></TableRow>
              <TableRow><TableCell>Hall of Fame</TableCell><TableCell>Aktuelles <DocLink to="hall-of-fame">Ranking</DocLink></TableCell></TableRow>
              <TableRow><TableCell>Letzte Änderungen</TableCell><TableCell>Zuletzt bearbeitete Datensätze</TableCell></TableRow>
              <TableRow><TableCell>Zuletzt benutzt</TableCell><TableCell>Links die letzten Audits, rechts die letzten Schwachstellen (mit Audit und Kunde). Die Anzahl stellen Sie im Widget ein (Standard 5).</TableCell></TableRow>
              <TableRow><TableCell>Aktuelle Diskussionen</TableCell><TableCell>Neueste <DocLink to="diskussion">Diskussionsbeiträge</DocLink></TableCell></TableRow>
              <TableRow><TableCell>Version &amp; Build</TableCell><TableCell>Installierte Programmversion</TableCell></TableRow>
              <TableRow><TableCell>System-Metriken</TableCell><TableCell>Systemkennzahlen (nur Administration)</TableCell></TableRow>
              <TableRow><TableCell>Offene Urlaubsanträge</TableCell><TableCell>Zu genehmigende Anträge (für Genehmiger)</TableCell></TableRow>
              <TableRow><TableCell>Externe Systeme</TableCell><TableCell>Status angebundener <DocLink to="debug-wartung">Monitoring-Dienste</DocLink> (nur Administration)</TableCell></TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <DocAlert severity="info">
          Manche Widgets erscheinen nur, wenn Ihre Rolle den passenden Bereich freischaltet (z. B. Review-Queue,
          Urlaubsanträge oder die administrativen Widgets).
        </DocAlert>

        <SubTitle id="dashboard-profile">Profile &amp; Bearbeiten</SubTitle>
        <Paragraph>
          Sie können mehrere <strong>Dashboard-Profile</strong> anlegen, umbenennen und zwischen ihnen wechseln – etwa ein
          Profil für die tägliche Arbeit und eines für administrative Aufgaben. Über <strong>Bearbeiten</strong> aktivieren
          Sie den Anordnungsmodus:
        </Paragraph>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 0.5, color: 'text.secondary' } }}>
          <li><strong>Anordnen &amp; Größe:</strong> Widgets per Ziehen verschieben und in der Größe anpassen.</li>
          <li><strong>Hinzufügen:</strong> über „Widget hinzufügen" – dasselbe Widget ist auch mehrfach möglich.</li>
          <li><strong>Entfernen:</strong> per Schließen-Symbol an der Kachel.</li>
        </Box>
        <Paragraph>
          Ihre Anordnung wird automatisch gespeichert und pro Profil gemerkt.
        </Paragraph>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  ZEITERFASSUNG                           */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="zeiterfassung">Zeiterfassung</SectionTitle>
        <Paragraph>
          Die Zeiterfassung ermöglicht es jedem Mitarbeiter, seine geleisteten Stunden projektbezogen zu dokumentieren.
        </Paragraph>
        <Paragraph>
          Ein Zeiteintrag umfasst:
        </Paragraph>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 0.5, color: 'text.secondary' } }}>
          <li><strong>Datum:</strong> An welchem Tag die Arbeit geleistet wurde</li>
          <li><strong>Projekt:</strong> Welchem <DocLink to="projekte">Projekt</DocLink> die Zeit zugeordnet wird</li>
          <li><strong>Audit (optional):</strong> Welchem <DocLink to="audits">Audit</DocLink> innerhalb des Projekts die Zeit gilt</li>
          <li><strong>Stunden:</strong> Anzahl der geleisteten Stunden</li>
          <li><strong>Beschreibung:</strong> Was wurde getan</li>
        </Box>
        <Paragraph>
          Die Zeiterfassung bietet eine Monatsübersicht, in der alle Einträge des aktuellen Monats zusammengefasst
          dargestellt werden. Erfasste Zeiten können bei der <DocLink to="rechnungen">Rechnungsstellung</DocLink> direkt
          referenziert werden, um die Abrechnung gegenüber dem Kunden transparent zu machen.
        </Paragraph>
        <DocAlert>
          Jeder Mitarbeiter sieht und bearbeitet seine eigenen Zeiteinträge. Management und Buchhaltung können
          zusätzlich die Zeiteinträge aller Mitarbeiter einsehen.
        </DocAlert>

        <Divider sx={{ my: 4 }} />

        <SectionTitle id="eingangsbelege">Eingangsbelege</SectionTitle>
        <Paragraph>
          Unter <MenuLink to="/eingangsbelege">Eingangsbelege</MenuLink> werden SaaS-Rechnungen gesammelt, die das
          Unternehmen selbst erhält – getrennt von den <DocLink to="rechnungen">Ausgangsrechnungen</DocLink> an Kunden.
          Der Menüpunkt erscheint mit der Berechtigung für eigene Belege, die Gesamtsicht oder die Buchhaltung.
        </Paragraph>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 0.8, color: 'text.secondary' } }}>
          <li><strong>Anbieter-Konten:</strong> je Konto ein Anbieter (derzeit Cursor oder ein sonstiger Anbieter), ein Verantwortlicher und der Synchronisationsstand (nicht verbunden, synchronisiert, Login erforderlich, Fehler).</li>
          <li><strong>Belege:</strong> abgerufene oder hochgeladene PDFs. Der Buchhaltungsstatus ist <Chip label="Neu" size="small" component="span" sx={{ mx: 0.5 }} />, <Chip label="Klärung" size="small" color="info" component="span" sx={{ mx: 0.5 }} /> oder <Chip label="Verbucht" size="small" color="success" component="span" sx={{ mx: 0.5 }} />.</li>
          <li><strong>Monatsübersicht:</strong> nur mit Gesamtsicht oder Buchhaltungsrecht. Sie zeigt, für welche Konten im Monat ein Beleg fehlt oder der Abruf hakt.</li>
        </Box>
        <Paragraph>
          Cursor-Rechnungen lassen sich über den Browser-Connector aus der bestehenden Browser-Sitzung abrufen oder als
          PDF hochladen. Für sonstige Anbieter ist der manuelle Upload vorgesehen.
        </Paragraph>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  URLAUB                                  */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="urlaub">Abwesenheiten</SectionTitle>
        <Paragraph>
          Unter <MenuLink to="/urlaub">Abwesenheiten</MenuLink> liegen drei getrennte Vorgänge: Urlaub, Krankheit und
          Workation. Jeder Reiter zeigt die eigenen Einträge; wer die passende Übersichtsberechtigung hat, sieht dort
          zusätzlich die Einträge aller Mitarbeiter.
        </Paragraph>

        <SubTitle id="abwesenheiten-urlaub">Urlaub</SubTitle>
        <Paragraph>
          Urlaub durchläuft einen Genehmigungsprozess und verbraucht ein jährliches Kontingent.
        </Paragraph>
        <Box component="ol" sx={{ pl: 3, mb: 3, '& li': { mb: 1, color: 'text.secondary' } }}>
          <li>Der Mitarbeiter wählt den gewünschten Zeitraum (Von/Bis) und gibt optional eine Bemerkung an.</li>
          <li>Der Antrag erhält den Status <Chip label="Beantragt" size="small" component="span" sx={{ mx: 0.5 }} />.</li>
          <li>Ein berechtigter Mitarbeiter (Berechtigung „Urlaub genehmigen“, typischerweise Admin und Management) kann den Antrag genehmigen oder ablehnen.</li>
          <li>Bei Genehmigung wechselt der Status zu <Chip label="Genehmigt" size="small" color="success" component="span" sx={{ mx: 0.5 }} />, bei Ablehnung zu <Chip label="Abgelehnt" size="small" color="error" component="span" sx={{ mx: 0.5 }} />.</li>
          <li>Optional kann der Genehmiger eine Admin-Bemerkung hinzufügen.</li>
        </Box>
        <Paragraph>
          Jeder Mitarbeiter hat ein jährliches Kontingent an Urlaubstagen (im Standard 30 Tage, pro Person einstellbar).
          Verbrauch und Resttage werden automatisch berechnet und im <DocLink to="dashboard">Dashboard</DocLink> angezeigt.
          Die teamweite Sicht liegt in der <DocLink to="urlaub-uebersicht">Abwesenheitsübersicht</DocLink>.
        </Paragraph>
        <DocAlert severity="info">
          Ob ein nicht verbrauchtes Kontingent automatisch ins Folgejahr übertragen wird, legt die Administration in der{' '}
          <DocLink to="systemkonfiguration">Systemkonfiguration</DocLink> fest.
        </DocAlert>

        <SubTitle id="abwesenheiten-krankheit">Krankheit</SubTitle>
        <Paragraph>
          Eine Krankmeldung hat kein Kontingent und keinen Genehmigungsschritt. Der Eintrag startet im Status{' '}
          <Chip label="Gemeldet" size="small" component="span" sx={{ mx: 0.5 }} /> und kann in diesem Status noch
          geändert oder gelöscht werden. In die Bemerkung gehören keine Diagnosen.
        </Paragraph>

        <SubTitle id="abwesenheiten-workation">Workation</SubTitle>
        <Paragraph>
          Workation ist ein Antrag ohne Kontingent: Sie bleiben arbeitsfähig, sind aber örtlich nicht einsetzbar.
          Der Status läuft wie beim Urlaub von <Chip label="Beantragt" size="small" component="span" sx={{ mx: 0.5 }} /> über{' '}
          <Chip label="Genehmigt" size="small" color="success" component="span" sx={{ mx: 0.5 }} /> oder{' '}
          <Chip label="Abgelehnt" size="small" color="error" component="span" sx={{ mx: 0.5 }} />.
          Genehmigen darf, wer die Berechtigung „Workation genehmigen“ hat (im Auslieferungszustand Admin und Management).
        </Paragraph>

        <Divider sx={{ my: 4 }} />

        <SectionTitle id="anwesenheit">Anwesenheit</SectionTitle>
        <Paragraph>
          Der <MenuLink to="/anwesenheit">Anwesenheitskalender</MenuLink> zeigt den Monat je Mitarbeiter. Genehmigter
          Urlaub und gemeldete Krankheit erscheinen gemeinsam als Fehlzeit, genehmigte Workation als eigene Markierung.
          Die Ansicht braucht die Berechtigung „Anwesenheitskalender“; im Auslieferungszustand haben sie alle Standardrollen.
        </Paragraph>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  HALL OF FAME                            */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="hall-of-fame">Hall of Fame</SectionTitle>
        <Paragraph>
          Die Hall of Fame ist ein Gamification-Element, das die Leistungen der Pentester honoriert. Basierend auf den
          gefundenen <DocLink to="schwachstellen">Schwachstellen</DocLink> wird ein Ranking erstellt – als Monatswertung,
          als Gesamtwertung (All-Time) und als Trendverlauf über mehrere Monate.
        </Paragraph>
        <Paragraph>
          <strong>Punktesystem</strong> (nach <DocLink to="schwachstellen-kritikalitaet">Kritikalität</DocLink>):
        </Paragraph>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Kritikalität</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Punkte</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow><TableCell><Chip label="Kritisch" size="small" sx={{ bgcolor: '#d32f2f', color: '#fff' }} /></TableCell><TableCell>10 Punkte</TableCell></TableRow>
              <TableRow><TableCell><Chip label="Hoch" size="small" sx={{ bgcolor: '#e65100', color: '#fff' }} /></TableCell><TableCell>7 Punkte</TableCell></TableRow>
              <TableRow><TableCell><Chip label="Mittel" size="small" sx={{ bgcolor: '#ed6c02', color: '#fff' }} /></TableCell><TableCell>4 Punkte</TableCell></TableRow>
              <TableRow><TableCell><Chip label="Niedrig" size="small" sx={{ bgcolor: '#2196f3', color: '#fff' }} /></TableCell><TableCell>2 Punkte</TableCell></TableRow>
              <TableRow><TableCell><Chip label="Info" size="small" sx={{ bgcolor: '#757575', color: '#fff' }} /></TableCell><TableCell>1 Punkt</TableCell></TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <DocAlert severity="success">
          In die Wertung fließen ausschließlich Schwachstellen mit dem Status <strong>Geprüft</strong> ein (also nach
          erfolgreicher <DocLink to="schwachstellen-review">Review-Freigabe</DocLink>). Entwürfe, eingereichte oder
          abgelehnte Funde zählen nicht.
        </DocAlert>
        <Paragraph>
          Die Monatswertung gruppiert die Funde nach ihrem <strong>Erstellungsdatum</strong>. Sind an einer Schwachstelle
          mehrere Mitarbeiter über eine <strong>Bounty-Verteilung</strong> beteiligt, werden die Punkte anteilig nach den
          hinterlegten Prozentwerten aufgeteilt; ohne Verteilung erhält der Reporter die vollen Punkte.
        </Paragraph>

        <Divider sx={{ my: 4 }} />

        <SectionTitle id="isolierte-umgebung">Isolierte Umgebung</SectionTitle>
        <Paragraph>
          Die <MenuLink to="/isolierte-umgebung">isolierte Umgebung</MenuLink> trennt Entwürfe von Findings, die noch
          keinem Audit gehören, vom übrigen Datenbestand. Im Hauptaccount prüfen Sie diese Sandbox-Findings, importieren
          sie in ein Audit, archivieren oder löschen sie und geben einzelne Audits oder Findings zum Lesen frei. Ein
          Zähler am Menüpunkt zeigt offene Freigaben.
        </Paragraph>
        <Paragraph>
          Mit dem <DocLink to="profil-sicherheit">Restricted-Passwort</DocLink> melden sich AI-Tools in einer
          eingeschränkten Sitzung an. Dort lassen sich eigene Findings anlegen, bearbeiten und archivieren; endgültig
          löschen kann nur der Hauptaccount. Zusätzlich sind die freigegebenen Audits und Findings lesbar. Eine
          API-Beschreibung für diese Sitzung liegt unter <InlineCode>/sandbox/api-docs</InlineCode>.
        </Paragraph>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  INTERNE DISKUSSION                      */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="diskussion">Interne Diskussion</SectionTitle>
        <Paragraph>
          Die interne Diskussion dient dem teaminternen Austausch – strukturiert nach <strong>Kategorien</strong> und{' '}
          <strong>Threads</strong>, unabhängig von der Kunde-Projekt-Hierarchie. Sichtbarkeit und Schreibrechte richten
          sich nach Ihrer Rollenzuweisung (Berechtigungen <InlineCode>Diskussion lesen</InlineCode>,{' '}
          <InlineCode>Beiträge schreiben</InlineCode>, <InlineCode>moderieren</InlineCode>).
        </Paragraph>

        <SubTitle id="diskussion-uebersicht">Überblick</SubTitle>
        <Paragraph>
          Über <MenuLink to="/diskussion">Diskussion</MenuLink> im Menü gelangen Sie zur Übersicht der Kategorien. Innerhalb
          einer Kategorie sehen Sie die Threads (mit Beitrags- und Antwortzählern), legen bei Bedarf neue Threads an und
          folgen der Konversation. Menüeintrag und Aktionen erscheinen nur, wenn Ihnen die passenden Rechte zugewiesen sind.
        </Paragraph>
        <Paragraph>
          <strong>Moderation:</strong> Mit Moderationsrecht lassen sich Kategorien anlegen und bearbeiten, Threads{' '}
          <strong>anheften</strong> oder <strong>sperren</strong> (gesperrte Threads erhalten keine neuen Antworten) sowie
          fremde Beiträge bearbeiten oder löschen.
        </Paragraph>

        <SubTitle id="diskussion-funktionen">Beiträge &amp; Funktionen</SubTitle>
        <Paragraph>
          Beiträge werden mit einem <strong>Rich-Text-Editor</strong> verfasst. Zur Verfügung stehen unter anderem:
        </Paragraph>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 0.8, color: 'text.secondary' } }}>
          <li><strong>Formatierung, Code &amp; Links</strong> sowie <strong>Bild-Uploads</strong> (auch per Einfügen/Paste aus der Zwischenablage).</li>
          <li><strong>Checklisten</strong> direkt im Beitrag – z. B. für Sammelaufgaben innerhalb eines Threads; Einträge können abgehakt werden.</li>
          <li><strong>Reaktionen:</strong> Beiträge lassen sich mit Emojis bewerten (👍 👎 ❤️ 😂 🎉 🤔 👀 ✅), eine Reaktion pro Person.</li>
          <li><strong>Zitieren:</strong> Beiträge können als Zitat in eine Antwort übernommen werden.</li>
          <li><strong>Thread beobachten:</strong> Sie werden über neue Beiträge in beobachteten Threads benachrichtigt.</li>
        </Box>

        <SubTitle id="diskussion-anker">Anker &amp; Erwähnungen</SubTitle>
        <Paragraph>
          <strong>Anker</strong> verknüpfen einen Thread optional mit Fachobjekten im System – etwa einem{' '}
          <DocLink to="projekte">Projekt</DocLink>, einem <DocLink to="audits">Audit</DocLink>, einer{' '}
          <DocLink to="schwachstellen">Schwachstelle</DocLink>, einem Kunden oder einem Tool. So bleibt der fachliche Kontext
          auffindbar, ohne die Diskussion in die Hierarchie „einzuklinken".
        </Paragraph>
        <Paragraph>
          Mit <strong>@Erwähnungen</strong> sprechen Sie Kolleginnen und Kollegen direkt an – die erwähnte Person wird
          (entsprechend ihrer Einstellungen) benachrichtigt.
        </Paragraph>
        <DocAlert severity="info">
          Wer per Beitrag, @Erwähnung oder Thread-Beobachtung benachrichtigt wird, steuern Sie in Ihren{' '}
          <DocLink to="profil-benachrichtigungen">Benachrichtigungseinstellungen</DocLink>.
        </DocAlert>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  PROFIL & SICHERHEIT                     */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="profil">Profil &amp; Sicherheit</SectionTitle>
        <Paragraph>
          Jeder Mitarbeiter hat ein persönliches Profil, das über das Benutzermenü oben rechts erreichbar ist. Dort pflegen
          Sie Ihre Daten, verwalten Sicherheitseinstellungen und steuern Ihre Benachrichtigungen.
        </Paragraph>

        <SubTitle id="profil-daten">Profil &amp; Sichtbarkeit</SubTitle>
        <Paragraph>
          Name, E-Mail und Ihre Rollen werden als Stammdaten angezeigt (nicht selbst änderbar). Darüber hinaus können Sie
          ein optionales <strong>Nutzerprofil</strong> pflegen – jeweils mit eigenem <strong>Sichtbarkeits-Schalter</strong>:
        </Paragraph>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 0.5, color: 'text.secondary' } }}>
          <li>Profilbild und Kontaktdaten</li>
          <li>Adresse und Erreichbarkeit (Zeitzone, Sprachen)</li>
          <li>Berufliches (Titel, LinkedIn, Website)</li>
          <li>Kompetenzen (Abschlüsse, Fähigkeiten)</li>
          <li>Persönliches (Geburtsdatum, „Über mich")</li>
        </Box>
        <DocAlert severity="info">
          Jeder Block ist <strong>standardmäßig privat</strong>. Nur freigegebene Inhalte erscheinen für andere angemeldete
          Nutzer im <strong>Kurzprofil</strong> (Klick auf einen Namen, z. B. in der <DocLink to="hall-of-fame">Hall of Fame</DocLink>,
          bei Reporter/Reviewer einer Schwachstelle oder in Zeiterfassungstabellen). Nicht freigegebene Profildaten sind auch
          für die Mitarbeiter-Verwaltung nicht einsehbar. Berufliche URLs müssen mit <code>https://</code> beginnen.
        </DocAlert>

        <SubTitle id="profil-sicherheit">Passwort &amp; 2FA</SubTitle>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 0.8, color: 'text.secondary' } }}>
          <li><strong>Passwort ändern:</strong> jederzeit möglich; ein neues Passwort muss der Passwort-Richtlinie genügen (u. a. Mindestlänge und Komplexität), die direkt bei der Eingabe geprüft wird.</li>
          <li><strong>Zwei-Faktor-Authentifizierung (2FA):</strong> TOTP-basiert (z. B. Google Authenticator, Authy). Die Einrichtung erfolgt per QR-Code; danach ist bei jedem Login ein 6-stelliger Code erforderlich. 2FA kann selbst aktiviert und wieder deaktiviert werden.</li>
          <li><strong>Eingeschränkter Zugang für AI-Tools:</strong> ein separates Restricted-Passwort (dieselbe Richtlinie, aber nicht identisch mit dem Hauptpasswort). Die Anmeldung braucht keinen 2FA-Code und öffnet nur die <DocLink to="isolierte-umgebung">isolierte Umgebung</DocLink>.</li>
        </Box>
        <DocAlert severity="warning">
          Hat die Administration die <strong>2FA-Pflicht</strong> für Ihr Konto aktiviert, werden Sie nach dem nächsten Login
          automatisch zur 2FA-Einrichtung geleitet. Ohne aktive 2FA ist dann kein Zugriff möglich.
        </DocAlert>

        <SubTitle id="profil-benachrichtigungen">Benachrichtigungseinstellungen</SubTitle>
        <Paragraph>
          Im Profil legen Sie pro Ereignis fest, über welche Kanäle Sie informiert werden möchten:
        </Paragraph>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 0.5, color: 'text.secondary' } }}>
          <li><strong>E-Mail</strong></li>
          <li><strong>Push</strong> (Gotify – Token/URL werden durch die Administration hinterlegt)</li>
          <li><strong>In-App</strong> (Glocken-Symbol in der oberen Leiste)</li>
        </Box>
        <Paragraph>
          Die Ereignisse sind nach Kategorien gruppiert (u. a. Audits, Schwachstellen, Rechnungen, Urlaub,{' '}
          <DocLink to="diskussion">Diskussion</DocLink>). Für Schwachstellen gibt es zusätzlich die Option, nur über{' '}
          <strong>direkt betreffende</strong> Ereignisse informiert zu werden. Hat die Administration verbindliche Vorgaben
          gesetzt, kann die Auswahl für einzelne Ereignisse gesperrt sein.
        </Paragraph>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  PROJEKTE                                */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="projekte">Projekte</SectionTitle>
        <Paragraph>
          Projekte sind der zentrale Organisationsrahmen für alle Audit-Aktivitäten. Jedes Projekt ist
          einem <DocLink to="kunden">Kunden</DocLink> zugeordnet und definiert den Umfang, das Team und die finanziellen Rahmenbedingungen.
        </Paragraph>

        <SubTitle id="projekte-typen">Projekttypen</SubTitle>
        <Paragraph>
          Es gibt drei Projekttypen, die bestimmen, wie Aufwände geplant und abgerechnet werden:
        </Paragraph>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Typ</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Beschreibung</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Typischer Einsatz</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell><Chip label="Single" size="small" color="primary" /></TableCell>
                <TableCell>Einmaliges Projekt mit fest definierten Personentagen (PT). Der Umfang steht von Beginn an fest.</TableCell>
                <TableCell>Einmaliger Penetrationstest, einzelner Security-Audit</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Kontingent" size="small" color="secondary" /></TableCell>
                <TableCell>Rahmenvertrag mit einem Kontingent an Personentagen, das über mehrere Audits hinweg abgerufen werden kann.</TableCell>
                <TableCell>Jahresverträge, wiederkehrende Prüfungen</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Festpreis" size="small" color="success" /></TableCell>
                <TableCell>Pauschales Angebot bzw. Projekt ohne PT-/Tagessatz-Planung. Der Betrag ergibt sich aus den Positionen (Einheit Pauschal). Keine Schnellabrechnung und keine Zeiterfassungs-Kontingentsteuerung.</TableCell>
                <TableCell>Festpreis-Audits, pauschale Pakete</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <SubTitle id="projekte-team">Projektteam</SubTitle>
        <Paragraph>
          Jedem Projekt wird ein <strong>Projektleiter</strong> zugewiesen, der die Verantwortung trägt.
          Zusätzlich können weitere <strong>Teammitglieder</strong> zugeordnet werden, die an den Audits des
          Projekts mitarbeiten.
        </Paragraph>
        <Paragraph>
          Wichtige Projektparameter:
        </Paragraph>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 0.5, color: 'text.secondary' } }}>
          <li><strong>Personentage (PT):</strong> Geplanter Gesamtaufwand in Tagen, auch halbe Tage (z.&nbsp;B. 6,5)</li>
          <li><strong>On-Site / Remote:</strong> Personentage und Tagessätze können getrennt erfasst werden, wenn Vor-Ort- und Remote-Sätze abweichen</li>
          <li><strong>Tagessatz:</strong> Finanzieller Tagessatz für die Abrechnung; bei getrennten Sätzen der gewichtete Mittelwert</li>
          <li><strong>Stunden pro PT:</strong> Umrechnungsfaktor (Standard: 8 Stunden = 1 PT)</li>
        </Box>

        <SubTitle id="projekte-status">Status</SubTitle>
        <Paragraph>
          Projekte durchlaufen verschiedene Statuswerte, die im <MenuLink to="/kanban">Kanban-Board</MenuLink> visualisiert
          werden. Die Statuswerte sind <strong>Stammdaten</strong> und werden in der{' '}
          <DocLink to="systemkonfiguration">Systemkonfiguration</DocLink> gepflegt – sie lassen sich also an Ihren Workflow
          anpassen. Im Auslieferungszustand sind dies:
        </Paragraph>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
          {['Geplant', 'In Durchführung', 'Reporting', 'Rechnungserstellung', 'Abgerechnet', 'Abgebrochen'].map((s) => (
            <Chip key={s} label={s} variant="outlined" size="small" />
          ))}
        </Box>
        <Paragraph>
          Offene <DocLink to="angebote">Angebote</DocLink> ohne verknüpftes Projekt erscheinen in der Projektliste und im Kanban
          mit dem Anzeige-Status <strong>Angebot</strong> (keine Stammdaten-Status, damit echte Projekte nicht auf
          „Angebot“ gesetzt werden können). Ein Klick öffnet das Angebot. Nach <DocLink to="angebote-projekt">Projekt erzeugen</DocLink>
          bleibt nur noch das echte Projekt in der Übersicht.
        </Paragraph>
        <DocAlert severity="info">
          Es gibt keinen erzwungenen Übergangs-Workflow: Der Projektstatus kann frei gesetzt werden – auch direkt per
          Drag-and-Drop im Kanban-Board.
        </DocAlert>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  KANBAN BOARD                            */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="kanban">Kanban Board</SectionTitle>
        <Paragraph>
          Das Kanban Board bietet eine visuelle Übersicht aller <MenuLink to="/projekte">Projekte</MenuLink>, organisiert nach ihrem aktuellen Status.
          Die erste Spalte <strong>Angebot</strong> zeigt offene Angebote (Entwurf, Gesendet, Gewonnen ohne Projekt);
          diese Karten sind nicht per Drag-and-Drop in Projektstatus verschiebbar. Projekte werden als Karten dargestellt
          und können per <strong>Drag-and-Drop</strong> zwischen den Statusspalten verschoben werden.
        </Paragraph>
        <Paragraph>
          Funktionen des Kanban Boards:
        </Paragraph>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 0.5, color: 'text.secondary' } }}>
          <li>Schnelle visuelle Übersicht über den <DocLink to="projekte-status">Projektstatus</DocLink></li>
          <li>Drag-and-Drop zum Ändern des Projektstatus</li>
          <li>Direkter Zugriff auf die <MenuLink to="/projekte">Projektdetails</MenuLink> per Klick</li>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  AUDITKALENDER                           */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="auditkalender">Auditkalender</SectionTitle>
        <Paragraph>
          Der Auditkalender zeigt alle geplanten und laufenden <MenuLink to="/audits">Audits</MenuLink> in einer Kalenderansicht an.
          Die Darstellung basiert auf den <DocLink to="audits-zeitraeume">Durchführungszeiträumen</DocLink> der einzelnen Audits.
        </Paragraph>
        <Paragraph>
          Der Kalender ermöglicht:
        </Paragraph>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 0.5, color: 'text.secondary' } }}>
          <li>Monatsansicht mit Wechsel von Monat und Jahr</li>
          <li>Durchführungszeiträume und Preflight-Checks im selben Raster</li>
          <li>Schnelles Erkennen von Überlappungen und Engpässen</li>
          <li>Direkter Zugriff auf <MenuLink to="/audits">Audit-Details</MenuLink> per Klick auf den Kalendereintrag</li>
          <li>Farbe nach Audit-Status; Preflight-Checks zusätzlich in Violett. Über die Legende lassen sich Geplant, In Durchführung, Abgeschlossen, Abgebrochen und Preflight-Check ein- und ausblenden.</li>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  AUDITS                                  */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="audits">Audits</SectionTitle>
        <Paragraph>
          Audits sind das Herzstück von STARK-AIO. Sie repräsentieren die tatsächliche Prüfungsaktivität –
          sei es ein Penetrationstest, ein Firewall-Audit oder ein Configuration Assessment.
          Jeder Audit gehört zu genau einem <DocLink to="projekte">Projekt</DocLink>.
        </Paragraph>

        <SubTitle id="audits-typen">Audit-Typen</SubTitle>
        <Paragraph>
          Jedem Audit können ein oder mehrere Typen zugeordnet werden, die die Art der Prüfung beschreiben. Die Typen sind{' '}
          <strong>Stammdaten</strong> (mit Name, Kürzel und Farbe) und werden in der <DocLink to="stammdaten">Stammdatenverwaltung</DocLink> gepflegt.
          Im Auslieferungszustand sind acht Typen vorbereitet:
        </Paragraph>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
          {[
            'Web Application Pentest',
            'Firewall Audit',
            'System Assessment',
            'API Pentest',
            'Praxisaudit',
            'Recon+ Intern',
            'Recon+ Extern',
            'Configuration Assessment',
          ].map((t) => (
            <Chip key={t} label={t} variant="outlined" size="small" />
          ))}
        </Box>

        <SubTitle id="audits-status">Status</SubTitle>
        <Paragraph>
          Der Status zeigt, in welcher Phase sich ein Audit befindet. Die Statuswerte sind <strong>Stammdaten</strong>
          (in der <DocLink to="systemkonfiguration">Systemkonfiguration</DocLink> sortier- und anpassbar). Standardmäßig
          gibt es folgende sieben Stufen:
        </Paragraph>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Nr.</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Beschreibung</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>0</TableCell>
                <TableCell><Chip label="Geplant" size="small" /></TableCell>
                <TableCell>Der Audit ist angelegt, Rahmenbedingungen sind definiert.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>1</TableCell>
                <TableCell><Chip label="Preflight Check" size="small" color="info" /></TableCell>
                <TableCell>Vorbereitende Prüfungen und Abstimmungen mit dem Kunden laufen.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>2</TableCell>
                <TableCell><Chip label="In Durchführung" size="small" color="warning" /></TableCell>
                <TableCell>Der Audit wird aktiv durchgeführt. <DocLink to="schwachstellen">Schwachstellen</DocLink> werden dokumentiert.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>3</TableCell>
                <TableCell><Chip label="Reporting" size="small" color="secondary" /></TableCell>
                <TableCell>Die Ergebnisse werden aufbereitet und der Bericht wird erstellt.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>4</TableCell>
                <TableCell><Chip label="Ergebnisbesprechung" size="small" color="primary" /></TableCell>
                <TableCell>Der Bericht wird mit dem Kunden besprochen und Maßnahmen werden abgestimmt.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>5</TableCell>
                <TableCell><Chip label="Beendet" size="small" color="success" /></TableCell>
                <TableCell>Der Audit ist abgeschlossen.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>6</TableCell>
                <TableCell><Chip label="Abgebrochen" size="small" color="error" /></TableCell>
                <TableCell>Der Audit wurde vorzeitig abgebrochen.</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <DocAlert severity="info">
          Die Reihenfolge oben beschreibt den typischen Ablauf. Es gibt jedoch <strong>keinen technisch erzwungenen
          Übergangs-Workflow</strong> – der Status kann jederzeit frei gesetzt werden (z. B. um einen Audit direkt auf
          „Abgebrochen" zu setzen).
        </DocAlert>

        <SubTitle id="audits-zeitraeume">Durchführungszeiträume</SubTitle>
        <Paragraph>
          Jeder Audit kann mehrere <strong>Durchführungszeiträume</strong> haben. Diese definieren die konkreten
          Zeitfenster, in denen der Audit stattfindet. Jeder Zeitraum hat ein Start- und Enddatum sowie
          eine optionale Beschreibung. Den Zeiträumen können auch spezifische Teammitglieder zugeordnet werden.
        </Paragraph>
        <Paragraph>
          Die Durchführungszeiträume sind die Basis für die Darstellung im <MenuLink to="/auditkalender">Auditkalender</MenuLink>.
        </Paragraph>

        <SubTitle id="audits-team">Audit-Team</SubTitle>
        <Paragraph>
          Unabhängig vom Projektteam kann jedem Audit ein eigenes Team zugewiesen werden. Das Audit-Team
          besteht aus Mitarbeitern, die aktiv an der Durchführung beteiligt sind. So können bei
          <DocLink to="projekte-typen">Kontingent-Projekten</DocLink> mit vielen Audits unterschiedliche Teams für verschiedene Audits eingesetzt werden.
        </Paragraph>

        <SubTitle id="audits-bericht">Bericht &amp; Demo-Modus</SubTitle>
        <Paragraph>
          Auf Basis der erfassten Daten (Management Summary, Vorbemerkungen, Schwachstellen mit Nachweisen) lässt sich der
          <strong> Audit-Bericht</strong> erzeugen (Berechtigung <InlineCode>Berichte generieren</InlineCode>). Ergänzend bietet
          STARK-AIO einen <strong>Demo-Modus</strong> – eine schreibgeschützte Vollbild-Präsentationsansicht des Audits für
          Kunden- oder Stakeholder-Termine:
        </Paragraph>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 0.5, color: 'text.secondary' } }}>
          <li><strong>Übersicht:</strong> Name, Projekt, Typen, Status, Team, Zeiträume und Management Summary</li>
          <li><strong>Statistik:</strong> Anzahl der Funde, CVSS-Kennzahlen sowie Diagramme nach Kritikalität und Review-Status</li>
          <li><strong>Schwachstellen:</strong> nummerierte Detailansicht mit Screenshot-Galerie (Lightbox)</li>
        </Box>

        <SubTitle id="audits-firewall">Firewall-Audit</SubTitle>
        <Paragraph>
          Trägt ein Audit den Typ <strong>Firewall Audit</strong> (oder das Kürzel <InlineCode>FW</InlineCode>), erscheint
          in der Detailansicht ein eigener Bereich. Pro VDOM werden drei Exporte erwartet: Regelwerk (policyList),
          Adressen und Interfaces. Den Typ erkennt STARK-AIO am Dateiinhalt.
        </Paragraph>
        <Paragraph>
          Bewertung und finaler Check stehen Personen mit der Rolle Pentester offen, die im Audit-Team stehen.
          Kommentarvorlagen und Risikoports dafür pflegt die <DocLink to="stammdaten">Stammdatenverwaltung</DocLink>.
          Feststellungen aus diesem Ablauf gehören zur Schwachstellenklasse <strong>Firewall Audit</strong>.
        </Paragraph>

        <SubTitle id="audits-historie">Versions- &amp; Änderungshistorie</SubTitle>
        <Paragraph>
          STARK-AIO protokolliert Änderungen auf zwei Ebenen, die in den Detailansichten von Audits und Schwachstellen
          (sowie weiteren Objekten) verfügbar sind:
        </Paragraph>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 0.8, color: 'text.secondary' } }}>
          <li><strong>Änderungshistorie:</strong> ein Protokoll aller Aktionen (erstellt, geändert, gelöscht, Datei hoch-/entfernt) – bei Änderungen feldweise mit Alt- und Neuwert.</li>
          <li><strong>Versionshistorie:</strong> inhaltliche Snapshots längerer Textfelder (z. B. Management Summary, Detailbeschreibung). Frühere Stände lassen sich ansehen und <strong>wiederherstellen</strong>.</li>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  SCHWACHSTELLEN                          */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="schwachstellen">Schwachstellen</SectionTitle>
        <Paragraph>
          Schwachstellen sind die konkreten Findings eines Audits. Jede Schwachstelle wird im Detail dokumentiert,
          bewertet und einem <DocLink to="schwachstellen-review">Status-Workflow mit 4-Augen-Prüfung</DocLink> unterzogen. Sie gehört immer zu genau einem <DocLink to="audits">Audit</DocLink>.
        </Paragraph>

        <SubTitle id="schwachstellen-kritikalitaet">Kritikalitätsstufen</SubTitle>
        <Paragraph>
          Jede Schwachstelle wird mit einer Kritikalitätsstufe und einem optionalen CVSS-Score bewertet. Die folgenden
          CVSS-Bereiche dienen als <strong>Orientierung</strong> – sie werden nicht technisch erzwungen:
        </Paragraph>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Stufe</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>CVSS (Orientierung)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}><DocLink to="hall-of-fame">Hall-of-Fame</DocLink>-Punkte</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Bedeutung</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell><Chip label="Kritisch" size="small" sx={{ bgcolor: '#d32f2f', color: '#fff' }} /></TableCell>
                <TableCell>9.0 – 10.0</TableCell>
                <TableCell>10</TableCell>
                <TableCell>Unmittelbare Gefahr, sofortige Maßnahmen erforderlich</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Hoch" size="small" sx={{ bgcolor: '#e65100', color: '#fff' }} /></TableCell>
                <TableCell>7.0 – 8.9</TableCell>
                <TableCell>7</TableCell>
                <TableCell>Schwerwiegendes Risiko, zeitnahe Behebung empfohlen</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Mittel" size="small" sx={{ bgcolor: '#ed6c02', color: '#fff' }} /></TableCell>
                <TableCell>4.0 – 6.9</TableCell>
                <TableCell>4</TableCell>
                <TableCell>Moderates Risiko, Behebung im nächsten Wartungszyklus</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Niedrig" size="small" sx={{ bgcolor: '#2196f3', color: '#fff' }} /></TableCell>
                <TableCell>1.0 – 3.9</TableCell>
                <TableCell>2</TableCell>
                <TableCell>Geringes Risiko, Verbesserungspotenzial</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Info" size="small" sx={{ bgcolor: '#757575', color: '#fff' }} /></TableCell>
                <TableCell>0.0</TableCell>
                <TableCell>1</TableCell>
                <TableCell>Informativ, kein direktes Sicherheitsrisiko</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Paragraph>
          Die Stufe <strong>Info</strong> wird intern teils auch als <em>Informational</em> bezeichnet – beide zählen gleich.
        </Paragraph>

        <SubTitle id="schwachstellen-klassen">Schwachstellenklassen</SubTitle>
        <Paragraph>
          Jede Schwachstelle wird optional einer Klasse zugeordnet, die den Typ des Sicherheitsproblems beschreibt. Die
          Klassen sind <strong>Stammdaten</strong> (mit deutscher und englischer Bezeichnung sowie einer allgemeinen
          Beschreibung) und orientieren sich an gängigen Standards wie den OWASP Top 10. Im Auslieferungszustand sind u. a.
          enthalten:
        </Paragraph>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
          {[
            'SQL Injection',
            'Cross-Site Scripting (XSS)',
            'Broken Authentication',
            'Insecure Direct Object Reference (IDOR)',
            'Security Misconfiguration',
            'Sensitive Data Exposure',
            'Server-Side Request Forgery (SSRF)',
            'Cross-Site Request Forgery (CSRF)',
            'Insufficient Logging & Monitoring',
            'Broken Access Control',
            'Cryptographic Failures',
            'Insecure Deserialization',
            'Using Components with Known Vulnerabilities',
            'Firewall Audit',
          ].map((c) => (
            <Chip key={c} label={c} variant="outlined" size="small" />
          ))}
        </Box>
        <Paragraph>
          Die hinterlegte allgemeine Beschreibung einer Klasse kann als Vorlage für das Reporting genutzt werden. In der{' '}
          <DocLink to="stammdaten">Stammdatenverwaltung</DocLink> steht zudem eine KI-gestützte Übersetzung (DE ↔ EN) bereit.
        </Paragraph>

        <SubTitle id="schwachstellen-review">Status-Workflow &amp; 4-Augen-Review</SubTitle>
        <Paragraph>
          Jede Schwachstelle durchläuft einen definierten <strong>Lebenszyklus</strong> nach dem <strong>4-Augen-Prinzip</strong>:
          Der Pentester, der einen Fund erfasst (<InlineCode>Reporter</InlineCode>), ist nicht derselbe, der ihn final freigibt
          (<InlineCode>Reviewer</InlineCode>). Neu erfasste Schwachstellen starten automatisch im Status <strong>Entwurf</strong>.
        </Paragraph>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Bedeutung</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Wer kann wechseln?</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell><Chip label="Entwurf" size="small" color="info" /></TableCell>
                <TableCell>Neu erfasst. Der Reporter arbeitet noch an der Dokumentation.</TableCell>
                <TableCell>Reporter → „Review erbeten"</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Review erbeten" size="small" color="warning" /></TableCell>
                <TableCell>Zur Prüfung eingereicht. Ein Reviewer kann den Fund nun begutachten (und optional „übernehmen").</TableCell>
                <TableCell>Reviewer → „Geprüft" oder „Abgelehnt"</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Geprüft" size="small" color="success" /></TableCell>
                <TableCell>Vom Reviewer bestätigt – bereit für den Bericht und für die <DocLink to="hall-of-fame">Hall of Fame</DocLink>.</TableCell>
                <TableCell>—</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Abgelehnt" size="small" color="error" /></TableCell>
                <TableCell>Vom Reviewer abgelehnt; die Begründung steht in der Zeitleiste. Der Reporter kann nacharbeiten.</TableCell>
                <TableCell>Reporter → „Review erbeten"</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 0.8, color: 'text.secondary' } }}>
          <li>Der <strong>Reviewer darf nicht der Reporter</strong> sein – nur so ist das 4-Augen-Prinzip gewahrt.</li>
          <li>Eine <strong>Ablehnung</strong> erfordert einen <strong>Begründungskommentar</strong>.</li>
          <li>Wird eine bereits <strong>geprüfte</strong> Schwachstelle inhaltlich geändert, wird sie automatisch auf <strong>Entwurf</strong> zurückgesetzt und muss erneut den Review durchlaufen.</li>
        </Box>
        <DocAlert severity="warning">
          Zum Einreichen benötigt der Reporter die Berechtigung <strong>Zur Prüfung einreichen</strong>, zum Freigeben oder
          Ablehnen der Reviewer die Berechtigung <strong>Schwachstellen reviewen</strong>. Typischerweise sind dies die
          Rollen <DocLink to="rollen-uebersicht">Pentester</DocLink> bzw. <DocLink to="rollen-uebersicht">Reviewer</DocLink>.
        </DocAlert>

        <SubTitle id="schwachstellen-tools">Tools, CWE &amp; Nachweise</SubTitle>
        <Paragraph>
          Zu jeder Schwachstelle können verwendete <strong>Tools</strong> und zutreffende <strong>Risikoszenarien</strong>
          zugeordnet werden (beides <DocLink to="stammdaten">Stammdaten</DocLink>); optional lässt sich eine
          <strong> CWE</strong>-Kennung hinterlegen. Diese Angaben fließen in den <DocLink to="audits-bericht">Audit-Bericht</DocLink> ein.
        </Paragraph>
        <Paragraph>
          Als <strong>Nachweise</strong> können Screenshots und Dateien direkt an der Schwachstelle hochgeladen werden; sie
          stehen so immer im richtigen Kontext zur Verfügung – etwa im Bericht oder im <DocLink to="audits-bericht">Demo-Modus</DocLink>.
        </Paragraph>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  REVIEWS                                 */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="reviews">Reviews</SectionTitle>
        <Paragraph>
          Die <strong>Review-Queue</strong> bündelt alle Schwachstellen, die aktuell auf eine Prüfung warten (Status{' '}
          <Chip label="Review erbeten" size="small" color="warning" component="span" sx={{ mx: 0.5 }} />). Sie ist nur für
          Rollen mit der Berechtigung <strong>Schwachstellen reviewen</strong> sichtbar; ein Zähler am Menüpunkt zeigt die
          Anzahl offener Reviews.
        </Paragraph>
        <Paragraph>
          Aus der Queue heraus öffnen Reviewer einen Fund, können ihn optional <strong>übernehmen</strong> („Claim", damit für
          andere sichtbar ist, dass die Prüfung läuft) und ihn anschließend freigeben oder mit Begründung ablehnen –
          gemäß dem <DocLink to="schwachstellen-review">4-Augen-Workflow</DocLink>.
        </Paragraph>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  WORKBENCH / TOOLBOX                     */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="workbench">Workbench (Toolbox)</SectionTitle>
        <Paragraph>
          Die Workbench – im Menü als <strong>Toolbox</strong> – bündelt Werkzeuge für die tägliche Pentest-Arbeit. Die
          Übersichtsseite zeigt alle Tools als Karten mit Kategorie und Kurzbeschreibung; per Stern markieren Sie{' '}
          <strong>Favoriten</strong> (auch als Dashboard-Widget verfügbar), und Sie können nach Name oder Kategorie filtern.
          Der gesamte Bereich erfordert die Berechtigung <strong>Workbench verwenden</strong>.
        </Paragraph>

        <SubTitle id="workbench-uebersicht">Übersicht &amp; Laufzeitmodell</SubTitle>
        <Paragraph>
          Die Tools unterscheiden sich darin, <em>wo</em> sie ausgeführt werden:
        </Paragraph>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 0.8, color: 'text.secondary' } }}>
          <li><strong>Serverseitig (Docker):</strong> Nmap, Subfinder und testssl.sh werden vom Backend in isolierten Containern ausgeführt.</li>
          <li><strong>Im Browser:</strong> CyberChef, Reverse-Shell-Generator, JWT-Debugger und der Nmap-XML-Viewer arbeiten vollständig lokal – ohne Datenübertragung an den Server.</li>
          <li><strong>Über externe Dienste:</strong> die PTA-Transkription nutzt das Backend als Vermittler zu Transkriptions- und KI-Diensten.</li>
          <li><strong>Integriert:</strong> der Draw.io-Editor speichert Diagramme serverseitig pro Benutzer.</li>
        </Box>
        <DocAlert severity="warning">
          Aktive Werkzeuge (z. B. Nmap, testssl.sh) dürfen nur gegen Ziele eingesetzt werden, für die eine ausdrückliche
          Beauftragung vorliegt.
        </DocAlert>

        <SubTitle id="workbench-scanning">Scanning &amp; Recon</SubTitle>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Tool</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Zweck</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow><TableCell>Nmap</TableCell><TableCell>Netzwerk-/Portscan gegen ein Ziel mit Presets (Quick, Full, Vuln) und erweiterten Optionen; läuft serverseitig.</TableCell></TableRow>
              <TableRow><TableCell>Subfinder</TableCell><TableCell>Passive Subdomain-Enumeration für eine Domain; Ergebnisliste mit CSV-Export.</TableCell></TableRow>
              <TableRow><TableCell>testssl.sh</TableCell><TableCell>Prüfung der TLS/SSL-Konfiguration eines Hosts (Protokolle, Cipher, Schwachstellen, HTTP-Header).</TableCell></TableRow>
              <TableRow><TableCell>Nmap XML Viewer</TableCell><TableCell>Bestehende Nmap-XML-Ergebnisse im Browser einlesen und aufbereiten (Hosts, Ports, Services); Export u. a. als URL-Liste.</TableCell></TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <SubTitle id="workbench-utilities">Utilities</SubTitle>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Tool</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Zweck</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow><TableCell>CyberChef</TableCell><TableCell>Encoder/Decoder-Pipeline (Base64, URL, Hex, Hashes u. v. m.) – komplett im Browser.</TableCell></TableRow>
              <TableRow><TableCell>Reverse Shell Generator</TableCell><TableCell>Erzeugt Reverse-Shell-Payloads für viele Sprachen/Plattformen aus LHOST/LPORT, optional kodiert.</TableCell></TableRow>
              <TableRow><TableCell>JWT Debugger</TableCell><TableCell>Dekodiert JSON Web Tokens (Header/Payload), zeigt Claims und Ablauf. Die Signatur wird nicht kryptografisch verifiziert.</TableCell></TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <SubTitle id="workbench-pta">PTA – Transkription &amp; Analyse</SubTitle>
        <Paragraph>
          <strong>PTA</strong> (Pentest Agreement) unterstützt das Aufbereiten von Besprechungen: Sie laden eine Audio- oder
          Videodatei hoch, lassen sie <strong>transkribieren</strong> (mit Sprechertrennung) und anschließend per KI in die
          relevanten PTA-Abschnitte und -Kapitel <strong>analysieren</strong>. Die geschätzten Kosten werden angezeigt.
        </Paragraph>
        <DocAlert severity="info">
          PTA nutzt externe Dienste über das Backend und ist nur verfügbar, wenn die entsprechenden Schnittstellen in der
          Server-Konfiguration hinterlegt sind. Der Aufruf ist sowohl über die Toolbox als auch direkt erreichbar.
        </DocAlert>

        <SubTitle id="workbench-drawio">Draw.io Editor</SubTitle>
        <Paragraph>
          Ein integrierter <strong>Diagramm-Editor</strong> (diagrams.net) für Netzwerk-, Ablauf- und UML-Diagramme.
          Diagramme werden <strong>pro Benutzer in der Datenbank gespeichert</strong> (mit Autosave) und lassen sich als PNG,
          SVG oder XML exportieren.
        </Paragraph>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  Interne Diskussion                    */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="kunden">Kunden &amp; Ansprechpartner</SectionTitle>

        <SubTitle id="kunden-uebersicht">Übersicht</SubTitle>
        <Paragraph>
          Kunden bilden die Grundlage für alle Projekte. Ein Kunde repräsentiert ein Unternehmen, für das
          IT-Security-Dienstleistungen erbracht werden.
        </Paragraph>
        <Paragraph>
          Zu jedem Kunden werden Stammdaten wie Unternehmensname, Adresse (Straße, PLZ, Ort, Land) sowie Rechnungskontakt und
          E-Mail erfasst. Für die <DocLink to="rechnungen-e-rechnung">E-Rechnung</DocLink> kommen USt-ID und optional die
          Leitweg-ID hinzu; Land und USt-ID schlagen den Steuerfall vor. Über die <MenuLink to="/kunden">Kundenliste</MenuLink> können Sie Kunden suchen, filtern und
          verwalten. Ein Klick auf einen Kunden öffnet die Detailansicht mit allen zugehörigen Projekten und
          Ansprechpartnern.
        </Paragraph>

        <SubTitle id="kunden-ansprechpartner">Ansprechpartner</SubTitle>
        <Paragraph>
          Jedem Kunden können beliebig viele Ansprechpartner zugeordnet werden. Diese dienen als Kontaktpersonen
          für die Kommunikation rund um Projekte und Audits.
        </Paragraph>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Feld</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Beschreibung</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow><TableCell>Vorname / Nachname</TableCell><TableCell>Vollständiger Name des Kontakts</TableCell></TableRow>
              <TableRow><TableCell>E-Mail</TableCell><TableCell>Geschäftliche E-Mail-Adresse</TableCell></TableRow>
              <TableRow><TableCell>Telefon / Mobil</TableCell><TableCell>Erreichbarkeit</TableCell></TableRow>
              <TableRow><TableCell>Position / Funktion</TableCell><TableCell>Rolle im Unternehmen (z. B. IT-Leiter, CISO)</TableCell></TableRow>
              <TableRow><TableCell>Abteilung</TableCell><TableCell>Zugehörige Organisationseinheit</TableCell></TableRow>
              <TableRow><TableCell>Büro / Standort</TableCell><TableCell>Physischer Standort des Kontakts</TableCell></TableRow>
              <TableRow><TableCell>Notizen</TableCell><TableCell>Freitextfeld für zusätzliche Informationen</TableCell></TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  ANGEBOTE                                */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="angebote">Angebote</SectionTitle>
        <Paragraph>
          Ein <strong>Angebot</strong> ist die kaufmännische Vorstufe zum Projekt: Zuerst wird die Nachfrage beim{' '}
          <DocLink to="kunden">Kunden</DocLink> erfasst, anschließend Positionen und PDF, danach der Vertriebsstatus.
          Ohne Kundenstammsatz gibt es kein Angebot – lose Leads ohne Kunde sind nicht vorgesehen. Projekte können
          weiterhin unabhängig angelegt werden; aus einem gewonnenen (oder bereits gesendeten) Angebot lässt sich
          ein Projekt explizit erzeugen. Bis dahin erscheinen offene Angebote in der <MenuLink to="/projekte">Projektliste</MenuLink>
          und im <MenuLink to="/kanban">Kanban</MenuLink> mit Status <strong>Angebot</strong>.
        </Paragraph>
        <Paragraph>
          Die Angebotsnummer folgt dem Format <InlineCode>A{'{YYYYMMDD}'}-{'{NNNN}'}</InlineCode>. Briefkopf und Absender
          stammen aus den bestehenden Rechnungseinstellungen. Es gibt keine ZUGFeRD-/E-Angebote und keinen
          E-Mail-Versand in dieser Version.
        </Paragraph>
        <Paragraph>
          Positionen entsprechen den <DocLink to="rechnungen-positionen">Rechnungspositionen</DocLink> (Beschreibung,
          Menge, Einheit PT/Stunden/Pauschal, Einzel- und Gesamtpreis). Netto, Steuerfall, Steuer und Brutto werden
          analog zur Rechnung berechnet. Personentage und Tagessatz werden aus den PT-Positionen abgeleitet und
          dienen als Vorlage beim Erzeugen des Projekts. Beim Typ <strong>Festpreis</strong> entfallen PT und
          Tagessatz; neue Positionen starten mit Einheit Pauschal.
        </Paragraph>
        <Paragraph>
          Beschreibung und Anschreiben erlauben eine kleine Textauszeichnung: <InlineCode>**fett**</InlineCode>,{' '}
          <InlineCode>*kursiv*</InlineCode>, Aufzählungen (<InlineCode>- Punkt</InlineCode>) und nummerierte
          Listen (<InlineCode>1. Punkt</InlineCode>). Dieselbe Auszeichnung erscheint im PDF. Überschriften,
          Tabellen, Links und Bilder sind nicht vorgesehen; ein allein stehender fetter Absatz reicht als
          Zwischenüberschrift. Positionsbezeichnungen bleiben einzeiliger Klartext.
        </Paragraph>

        <SubTitle id="angebote-status">Angebotsstatus</SubTitle>
        <Paragraph>
          Der Status läuft auf dem Datensatz (Liste mit Filter und Karten, kein zweites Kanban). Abgelaufenes
          Gültigkeitsdatum bei Status <strong>Gesendet</strong> wird in der Oberfläche gewarnt, ändert den Status aber
          nicht automatisch.
        </Paragraph>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Bedeutung</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Mögliche Übergänge</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell><Chip label="Entwurf" size="small" /></TableCell>
                <TableCell>Nachfrage und Angebot werden erfasst; nur hier ist Bearbeiten und Löschen möglich.</TableCell>
                <TableCell>→ Gesendet, → Zurückgezogen</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Gesendet" size="small" color="info" /></TableCell>
                <TableCell>Kaufmännisches Angebot liegt beim Kunden.</TableCell>
                <TableCell>→ Gewonnen, → Verloren, → Zurückgezogen, → Entwurf</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Gewonnen" size="small" color="success" /></TableCell>
                <TableCell>Beauftragung; Endstatus. Optional verknüpftes Projekt.</TableCell>
                <TableCell>— (Endstatus)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Verloren" size="small" color="error" /></TableCell>
                <TableCell>Abgelehnt; optionaler Ablehnungsgrund.</TableCell>
                <TableCell>— (Endstatus)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Zurückgezogen" size="small" /></TableCell>
                <TableCell>Intern zurückgezogen; optionaler Grund.</TableCell>
                <TableCell>— (Endstatus)</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <DocAlert severity="info">
          Der Wechsel <strong>Gesendet → Entwurf</strong> erlaubt Nachbesserungen. Es gibt keine Angebots-Versionierung.
          Statuswechsel benötigen die Berechtigung <strong>Angebotsstatus ändern</strong>.
        </DocAlert>

        <SubTitle id="angebote-projekt">Projekt erzeugen</SubTitle>
        <Paragraph>
          Über <strong>Projekt erzeugen</strong> (Berechtigungen <strong>Angebote bearbeiten</strong> und{' '}
          <strong>Projekte anlegen</strong>) wird ab Status <strong>Gesendet</strong> oder <strong>Gewonnen</strong> ein
          Projekt angelegt: Name aus dem Titel, Kunde, Beschreibung, Typ, Personentage, Tagessatz und Stunden pro PT,
          Status <strong>Geplant</strong>. Ist bereits ein Projekt verknüpft, wird dasselbe Projekt zurückgegeben
          (idempotent). Optional wird der Angebotsstatus dabei auf <strong>Gewonnen</strong> gesetzt. Danach erscheint das
          Angebot nicht mehr in der Projektliste/Kanban-Spalte <strong>Angebot</strong>, sondern nur noch das erzeugte
          Projekt (Status <strong>Geplant</strong>). Eine Rechnung
          entsteht nicht direkt aus dem Angebot – sie braucht weiterhin ein Projekt. Beim Anlegen einer Rechnung zu
          diesem Projekt wird die <strong>Angebotsnummer</strong> (ZUGFeRD BT-14) automatisch übernommen.
        </Paragraph>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  RECHNUNGEN                              */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="rechnungen">Rechnungen</SectionTitle>
        <Paragraph>
          Das Rechnungsmodul ermöglicht die Abrechnung von Projekten. Jede Rechnung gehört zu
          genau einem <DocLink to="projekte">Projekt</DocLink> und kann mehrere Positionen enthalten.
          Gehört zum Projekt ein <DocLink to="angebote">Angebot</DocLink>, wird die Angebotsnummer beim Anlegen
          vorbelegt.
        </Paragraph>

        <SubTitle id="rechnungen-positionen">Rechnungspositionen</SubTitle>
        <Paragraph>
          Eine Rechnung besteht aus einer oder mehreren Positionen, die den abzurechnenden Umfang beschreiben:
        </Paragraph>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Feld</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Beschreibung</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow><TableCell>Beschreibung</TableCell><TableCell>Textuelle Beschreibung der Leistung</TableCell></TableRow>
              <TableRow><TableCell>Menge</TableCell><TableCell>Anzahl der abgerechneten Einheiten</TableCell></TableRow>
              <TableRow><TableCell>Einheit</TableCell><TableCell><InlineCode>PT</InlineCode> (Personentage), <InlineCode>Stunden</InlineCode> oder <InlineCode>Pauschal</InlineCode></TableCell></TableRow>
              <TableRow><TableCell>Einzelpreis</TableCell><TableCell>Preis pro Einheit</TableCell></TableRow>
              <TableRow><TableCell>Gesamtpreis</TableCell><TableCell>Automatisch berechnet (Menge × Einzelpreis)</TableCell></TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Paragraph>
          <DocLink to="zeiterfassung">Zeiteinträge</DocLink> können direkt einer Rechnung zugeordnet werden, um den Zusammenhang
          zwischen geleisteter Arbeit und Abrechnung herzustellen.
        </Paragraph>

        <SubTitle id="rechnungen-status">Rechnungsstatus</SubTitle>
        <Paragraph>
          Jede Rechnung durchläuft einen Lebenszyklus. Anders als beim Audit-Status sind die Übergänge hier{' '}
          <strong>fest definiert</strong>:
        </Paragraph>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Bedeutung</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Mögliche Übergänge</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell><Chip label="Entwurf" size="small" /></TableCell>
                <TableCell>Rechnung wird erstellt und kann frei bearbeitet werden.</TableCell>
                <TableCell>→ Versendet, → Storniert</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Versendet" size="small" color="info" /></TableCell>
                <TableCell>Rechnung wurde an den Kunden versendet.</TableCell>
                <TableCell>→ Bezahlt, → Storniert</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Bezahlt" size="small" color="success" /></TableCell>
                <TableCell>Zahlungseingang wurde verbucht.</TableCell>
                <TableCell>— (Endstatus)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Storniert" size="small" color="error" /></TableCell>
                <TableCell>Rechnung wurde storniert und ist ungültig.</TableCell>
                <TableCell>— (Endstatus)</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <DocAlert severity="info">
          Eine Rechnung lässt sich nur im Status <strong>Entwurf</strong> bearbeiten oder löschen. Der Statuswechsel
          erfordert die Berechtigung <strong>Rechnungsstatus ändern</strong>.
        </DocAlert>
        <Paragraph>
          Weitere Rechnungsdetails umfassen die <strong>Rechnungsnummer</strong> (eindeutig), den{' '}
          <strong>Leistungszeitraum</strong>, das <strong>Zahlungsziel</strong> (Standard: 30 Tage) sowie automatisch
          berechnete Netto-, Steuer- und Bruttobeträge. Der Steuersatz hängt vom Steuerfall ab.
        </Paragraph>

        <SubTitle id="rechnungen-e-rechnung">E-Rechnung &amp; Aufteilung</SubTitle>
        <Paragraph>
          Neben dem PDF lässt sich eine <strong>E-Rechnung</strong> erzeugen (ZUGFeRD 2.x, PDF/A-3 mit eingebetteter XML,
          Profil EN 16931). Absenderdaten dafür – Firma, Adresse, USt-ID, Bankverbindung – pflegt die Administration im
          Reiter <strong>E-Rechnung</strong> der <DocLink to="systemkonfiguration">Systemkonfiguration</DocLink>. Ohne die
          Pflichtangaben schlägt die Erzeugung fehl. Anschreiben und Zahlungshinweise kommen aus den{' '}
          <DocLink to="rechnungsvorlagen">Rechnungsvorlagen</DocLink>.
        </Paragraph>
        <Paragraph>
          Der Steuerfall wird aus Land und USt-ID des Rechnungsempfängers vorgeschlagen und kann überschrieben werden:
        </Paragraph>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 0.5, color: 'text.secondary' } }}>
          <li><strong>Inland:</strong> 19 % Umsatzsteuer</li>
          <li><strong>EU mit USt-ID:</strong> Reverse Charge, 0 %</li>
          <li><strong>EU ohne USt-ID:</strong> 19 %, weil Reverse Charge dann nicht möglich ist</li>
          <li><strong>Drittland:</strong> nicht steuerbar, 0 %</li>
        </Box>
        <Paragraph>
          Eine Rechnung im Status Entwurf lässt sich auf zwei Empfänger aufteilen. Dabei entstehen zwei eigene Rechnungen
          mit eigenen Nummern, PDFs und E-Rechnungen; die Anteile bleiben über die Partnerrechnung verknüpft.
        </Paragraph>

        <Divider sx={{ my: 4 }} />

        <SectionTitle id="rechnungsvorlagen">Rechnungsvorlagen</SectionTitle>
        <Paragraph>
          Unter <MenuLink to="/rechnungen/textvorlagen">Rechnungsvorlagen</MenuLink> (Berechtigung{' '}
          <strong>Textvorlagen der Ausgangsrechnungen verwalten</strong>) liegen benannte Textbausteine für Ausgangsrechnungen und E-Rechnungen:
          Anrede, Anschreiben, Grußformel, Unterzeichner, Leistungsnachweis- und Zahlungshinweis. Eine Vorlage kann als
          Standard gelten und einzelnen Kunden zugeordnet werden. Beim Anlegen einer Rechnung wird die passende Vorlage
          angeboten.
        </Paragraph>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  Datenimport & -export                  */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="mitarbeiter">Mitarbeiterverwaltung</SectionTitle>
        <Paragraph>
          Unter <MenuLink to="/mitarbeiter">Mitarbeiter</MenuLink> werden die Benutzerkonten verwaltet. Die Liste zeigt Name,
          E-Mail, zugewiesene Rollen und den Aktiv-Status. In der Detailansicht legen Sie Konten an, bearbeiten oder
          deaktivieren sie.
        </Paragraph>
        <Paragraph>
          Pro Mitarbeiter werden u. a. gepflegt:
        </Paragraph>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 0.5, color: 'text.secondary' } }}>
          <li><strong>Stammdaten:</strong> Vorname, Nachname, E-Mail</li>
          <li><strong>Rollen:</strong> eine oder mehrere <DocLink to="rollen">Rollen</DocLink> (Mehrfachauswahl)</li>
          <li><strong>Urlaub:</strong> Urlaubstage pro Jahr, Eintrittsdatum sowie eine manuelle Übertrags-Korrektur</li>
          <li><strong>Aktiv-Status:</strong> Konten lassen sich deaktivieren statt löschen</li>
        </Box>
        <Paragraph>
          Sicherheitsfunktionen pro Konto: <strong>2FA-Status</strong> einsehen, <strong>2FA-Pflicht</strong> erzwingen oder
          2FA zurücksetzen sowie einen <strong>Passwort-Reset</strong> per E-Mail auslösen. Bei der Neuanlage erhält der
          Mitarbeiter automatisch eine Willkommens-E-Mail. Zusätzlich können pro Person Benachrichtigungs-Einstellungen
          (Kanäle, Gotify-Zugang) hinterlegt werden.
        </Paragraph>
        <DocAlert severity="info">
          Die Mitarbeiterverwaltung erfordert die entsprechende Berechtigung (Anlegen/Bearbeiten/Löschen separat steuerbar).
          Nicht freigegebene <DocLink to="profil-daten">Profildaten</DocLink> einer Person sind auch hier nicht einsehbar.
        </DocAlert>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  URLAUBSÜBERSICHT                        */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="urlaub-uebersicht">Abwesenheitsübersicht</SectionTitle>
        <Paragraph>
          Die <MenuLink to="/urlaub-uebersicht">Abwesenheitsübersicht</MenuLink> ist die Team-Sicht auf{' '}
          <DocLink to="urlaub">Abwesenheiten</DocLink>. Urlaub aller Mitarbeiter sehen Rollen mit „Urlaub aller einsehen“
          (im Auslieferungszustand Admin und Management), inklusive Status, Zeiträumen und Resttagen. Dieselbe Seite zeigt
          offene Workation-Anträge und Krankmeldungen, sofern die jeweilige Übersichtsberechtigung vorliegt. Genehmigen
          von Urlaub und Workation erfolgt von hier aus.
        </Paragraph>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  STAMMDATEN                              */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="stammdaten">Stammdaten</SectionTitle>
        <Paragraph>
          Unter <MenuLink to="/verwaltung">Stammdaten</MenuLink> werden die fachlichen Katalogdaten gepflegt, die an anderer
          Stelle ausgewählt werden können. Die Seite ist in Reiter gegliedert:
        </Paragraph>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Bereich</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Felder / Besonderheiten</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow><TableCell><DocLink to="audits-typen">Audit-Typen</DocLink></TableCell><TableCell>Name, Kürzel (1–4 Zeichen) und Farbe</TableCell></TableRow>
              <TableRow><TableCell><DocLink to="schwachstellen-klassen">Schwachstellenklassen</DocLink></TableCell><TableCell>Bezeichnung und Beschreibung (DE/EN), mit KI-Übersetzung</TableCell></TableRow>
              <TableRow><TableCell>Tools</TableCell><TableCell>Werkzeuge, die Schwachstellen zugeordnet werden</TableCell></TableRow>
              <TableRow><TableCell>Risikoszenarien</TableCell><TableCell>Bezeichnung (DE/EN), mit KI-Übersetzung</TableCell></TableRow>
              <TableRow><TableCell>Firewall-Kommentare</TableCell><TableCell>Kommentarvorlagen für die <DocLink to="audits-firewall">Firewall-Bewertung</DocLink></TableCell></TableRow>
              <TableRow><TableCell>Firewall-Risikoports</TableCell><TableCell>Ports, die in der Firewall-Bewertung hervorgehoben werden</TableCell></TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <DocAlert severity="info">
          Die Status-Stammdaten für <DocLink to="projekte-status">Projekte</DocLink> und <DocLink to="audits-status">Audits</DocLink>
          werden nicht hier, sondern in der <DocLink to="systemkonfiguration">Systemkonfiguration</DocLink> gepflegt.
        </DocAlert>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  Datenimport & -export                  */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="daten-transfer">Datenimport &amp; -export</SectionTitle>
        <Paragraph>
          Über <MenuLink to="/daten">Datenimport/-export</MenuLink> lassen sich ausgewählte fachliche Domänendaten
          (Stammdaten, Kunden, Projekte, Audits, Schwachstellen, Dateien u. a.) exportieren und in einer anderen Umgebung
          wieder importieren – nützlich für Staging, lokale Entwicklung oder vorsichtige Synchronisation. Export und Import
          sind über getrennte Berechtigungen steuerbar.
        </Paragraph>
        <DocAlert severity="warning">
          Dies ist <strong>kein</strong> vollständiges Infrastruktur-Backup (dafür gibt es die <DocLink to="backups">Backups</DocLink>).
          Technische Geheimnisse (z. B. Sitzungen, 2FA-Secrets) werden in der Regel <strong>nicht</strong> exportiert.
          Importierte Mitarbeiterkonten erhalten ein vorgegebenes Erstpasswort.
        </DocAlert>

        <SubTitle id="daten-transfer-ablauf">Ablauf &amp; Hinweise</SubTitle>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1, color: 'text.secondary' } }}>
          <li>
            <strong>Export:</strong> Entitätsgruppen wählen (optional inkl. Abhängigkeiten). Ohne Dateianhänge entsteht eine
            <strong> JSON</strong>-Datei, mit Dateien ein <strong>ZIP</strong>-Archiv (Daten + Dateiablage).
          </li>
          <li>
            <strong>Import:</strong> JSON- oder ZIP-Datei hochladen und eine Strategie wählen: <strong>Bestehende überspringen</strong>,
            <strong> Überschreiben</strong> oder <strong>Bei Konflikt abbrechen</strong>. Das Ergebnis weist importierte,
            übersprungene und fehlerhafte Einträge je Entität aus.
          </li>
          <li>
            <strong>Verweise:</strong> Beim Import werden alle IDs auf die Zielumgebung umgeschrieben. Damit Verweise wie
            Rechnungsempfänger, Textvorlage oder die Rechnungszuordnung von Zeiteinträgen erhalten bleiben, sollten die
            Abhängigkeiten mit exportiert werden. Fehlt eine verwiesene Entität, wird der Datensatz mit einer Meldung
            übersprungen oder der Verweis bewusst geleert.
          </li>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  KUNDENUPLOADS                           */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="kundenuploads">Kundenuploads</SectionTitle>
        <Paragraph>
          Über <MenuLink to="/kundenuploads">Kundenuploads</MenuLink> können Sie Kunden eine sichere Möglichkeit geben,
          Dateien hochzuladen – ohne eigenen Zugang zu STARK-AIO. Dazu wird eine <strong>Upload-Session</strong> mit
          Beschreibung und Gültigkeitsdatum erstellt; das System erzeugt dafür automatisch einen <strong>Link</strong> und ein
          <strong> Passwort</strong>.
        </Paragraph>
        <Box component="ol" sx={{ pl: 3, mb: 3, '& li': { mb: 0.8, color: 'text.secondary' } }}>
          <li>Session anlegen und Link sowie Passwort an den Kunden weitergeben.</li>
          <li>Der Kunde öffnet die öffentliche Upload-Seite (<InlineCode>/upload/&lt;Token&gt;</InlineCode>), gibt das Passwort ein und lädt Dateien per Drag-and-Drop hoch.</li>
          <li>Hochgeladene Dateien erscheinen in der Session-Detailansicht und können einzeln oder als ZIP heruntergeladen werden.</li>
        </Box>
        <DocAlert severity="info">
          Sessions haben einen Status (neu / hochgeladen / abgelaufen) und ein Ablaufdatum. Das Anlegen und Verwalten
          erfordert die entsprechende Berechtigung; das Hochladen durch den Kunden ist über das Session-Passwort geschützt.
        </DocAlert>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  Rollen & Berechtigungen                 */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="systemkonfiguration">Systemkonfiguration</SectionTitle>
        <Paragraph>
          Die <MenuLink to="/systemkonfiguration">Systemkonfiguration</MenuLink> bündelt zentrale System- und
          Facheinstellungen (Berechtigung <strong>Verwaltung verwalten</strong>). Sie ist in Reiter gegliedert:
        </Paragraph>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 0.8, color: 'text.secondary' } }}>
          <li><strong>Projektstatus &amp; Auditstatus:</strong> Anlegen, Bearbeiten, Löschen und Sortieren der Status-Stammdaten für <DocLink to="projekte-status">Projekte</DocLink> und <DocLink to="audits-status">Audits</DocLink>.</li>
          <li><strong>Push (Gotify):</strong> Basis-URL und Versandmodus für Push-Benachrichtigungen.</li>
          <li><strong>E-Mail (Amazon SES):</strong> Versandmodus, Empfänger-Allowlist, Region, Absender und Zugangsdaten.</li>
          <li><strong>Urlaub:</strong> automatischer Vorjahres-Übertrag und eine Planungs-Hinweisfrist.</li>
          <li><strong>E-Rechnung:</strong> Absenderstammdaten für PDF und <DocLink to="rechnungen-e-rechnung">ZUGFeRD</DocLink> (Firma, Adresse, USt-ID, Bankverbindung, Handelsregister). Anschreiben und Zahlungshinweise liegen unter <DocLink to="rechnungsvorlagen">Rechnungsvorlagen</DocLink>.</li>
          <li><strong>Gimmicks:</strong> Schalter für den Hinweis am 1. April und für saisonale Zeilen (Karneval, Ostern, Pfingsten, Halloween, Winter, Weihnachten, Neujahr). Berichte, Rechnungen und Kundenansichten sind davon ausgenommen.</li>
        </Box>
        <DocAlert severity="info">
          Passwort-Richtlinie und 2FA-Pflicht werden nicht hier, sondern je Mitarbeiter in der{' '}
          <DocLink to="mitarbeiter">Mitarbeiterverwaltung</DocLink> gesteuert.
        </DocAlert>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  E-MAIL-TEMPLATES                        */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="email-templates">E-Mail-Templates</SectionTitle>
        <Paragraph>
          Die Seite <MenuLink to="/email-templates">E-Mail-Templates</MenuLink> zeigt eine Übersicht aller systemweiten
          E-Mail-Vorlagen mit Betreff, verwendeten Variablen und einer HTML-Vorschau. Vorlagen existieren u. a. für
          Konto-/Passwort-Vorgänge, Urlaubsanträge, Audit-, Projekt- und Schwachstellen-Ereignisse, Rechnungen sowie
          System-Meldungen (z. B. Backups).
        </Paragraph>
        <DocAlert severity="info">
          Die Templates dienen der <strong>Ansicht/Vorschau</strong>; sie werden hier nicht bearbeitet.
        </DocAlert>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  BACKUPS                                 */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="backups">Backups</SectionTitle>
        <Paragraph>
          Unter <MenuLink to="/backups">Backups</MenuLink> sehen Sie die vorhandenen Sicherungen und können ein Backup
          <strong> manuell</strong> anstoßen. Optionen dabei: <strong>nur lokal</strong> (kein Upload zum externen Speicher)
          und <strong>Logs mitsichern</strong>. Gesichert werden die Datenbank und das Upload-Verzeichnis; die Liste zeigt
          Dateiname, Datum, Größe und Quelle (lokal / externer Speicher) und lässt sich filtern.
        </Paragraph>
        <DocAlert severity="info">
          Bei Erfolg oder Fehler werden Administratoren automatisch benachrichtigt. Eine Rücksicherung (Restore) erfolgt
          serverseitig und ist nicht Teil dieser Oberfläche.
        </DocAlert>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  BERECHTIGUNGSMATRIX                     */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="berechtigungsmatrix">Berechtigungsmatrix</SectionTitle>
        <Paragraph>
          Die <MenuLink to="/berechtigungsmatrix">Berechtigungsmatrix</MenuLink> ist das Werkzeug zur Verwaltung von Rollen
          und Rechten. In einer Tabelle (Rollen × Berechtigungen, nach Kategorien gruppiert) aktivieren oder deaktivieren Sie
          jede Berechtigung per Checkbox; Änderungen werden sofort gespeichert. Rollen lassen sich anlegen, umbenennen und
          löschen.
        </Paragraph>
        <DocAlert severity="info">
          Die Rolle <strong>Admin</strong> ist eine Systemrolle: Sie hat immer alle Rechte und kann nicht bearbeitet werden.
          Eine ausführliche Beschreibung der Rollen finden Sie unter <DocLink to="rollen">Rollen &amp; Berechtigungen</DocLink>.
        </DocAlert>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  DEBUG & WARTUNG                         */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="debug-wartung">Debug &amp; Wartung</SectionTitle>
        <Paragraph>
          Die Seite <MenuLink to="/debug-wartung">Debug &amp; Wartung</MenuLink> bündelt Diagnose- und Wartungswerkzeuge:
        </Paragraph>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 0.8, color: 'text.secondary' } }}>
          <li><strong>E-Mail-Debug:</strong> Test-E-Mails über Amazon SES versenden und den Versandverlauf nachvollziehen.</li>
          <li><strong>Monitoring-Services:</strong> externe Überwachungs-Server verwalten und deren Verbindung testen; ihre Umgebungen erscheinen im <DocLink to="dashboard">Dashboard</DocLink> unter „Externe Systeme".</li>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* ──────────────────────────────────────── */}
        {/*  ROLLEN & BERECHTIGUNGEN                 */}
        {/* ──────────────────────────────────────── */}
        <SectionTitle id="rollen">Rollen &amp; Berechtigungen</SectionTitle>
        <Paragraph>
          STARK-AIO nutzt ein rollenbasiertes Berechtigungssystem. Jeder Mitarbeiter kann eine oder mehrere Rollen
          erhalten; die effektiven Rechte ergeben sich aus der Vereinigung aller zugewiesenen Rollen und bestimmen, welche
          Funktionen und Daten sichtbar sind.
        </Paragraph>
        <DocAlert severity="info">
          Die folgenden Tabellen zeigen den <strong>Auslieferungszustand</strong> der fünf Standardrollen. Rollen und Rechte
          sind über die <DocLink to="berechtigungsmatrix">Berechtigungsmatrix</DocLink> frei konfigurierbar – ein produktives
          System kann daher abweichen. <strong>Admin</strong> ist eine Systemrolle und hat immer alle Rechte.
        </DocAlert>

        <SubTitle id="rollen-uebersicht">Rollenübersicht</SubTitle>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Rolle</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Beschreibung</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell><Chip label="Admin" size="small" color="error" /></TableCell>
                <TableCell>Systemrolle mit Vollzugriff auf alle Funktionen und Einstellungen – inkl. Mitarbeiter-, Rollen-, Stammdaten- und Systemverwaltung.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Management" size="small" color="primary" /></TableCell>
                <TableCell>Voller Zugriff auf Projekte, Kunden und Angebote (inkl. Statuswechsel und Projekt aus Angebot). Audits anlegen und bearbeiten, aber nicht löschen. Kann Urlaub und Workation genehmigen, Krankmeldungen aller einsehen, alle Zeiteinträge einsehen, Stammdaten pflegen, Daten im- und exportieren, Kundenuploads verwalten, eigene Eingangsbelege führen, die Workbench nutzen und die Diskussion moderieren. Rechnungen und Schwachstellen nur lesend.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Pentester" size="small" color="warning" /></TableCell>
                <TableCell>Kann zugewiesene Projekte und Audits einsehen, Schwachstellen erfassen, bearbeiten, eigene löschen und zur Prüfung einreichen, eigene Zeiteinträge verwalten, Berichte generieren, die Workbench nutzen, Daten exportieren, Kundenuploads verwalten, eigene Eingangsbelege führen sowie eigene Abwesenheiten und den Anwesenheitskalender nutzen.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Reviewer" size="small" color="secondary" /></TableCell>
                <TableCell>Kann Audits einsehen, Schwachstellen reviewen (4-Augen-Prinzip), Berichte generieren, eigene Zeiteinträge verwalten, die Workbench nutzen, eigene Eingangsbelege führen sowie eigene Abwesenheiten und den Anwesenheitskalender nutzen.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Buchhaltung" size="small" color="success" /></TableCell>
                <TableCell>Voller Zugriff auf Rechnungen (inkl. Statuswechsel und Rechnungsvorlagen) und auf Eingangsbelege inklusive Buchhaltungsstatus. Kann Angebote, Projekte und Kunden einsehen, alle Zeiteinträge einsehen, eigene Zeiten erfassen sowie eigene Abwesenheiten und den Anwesenheitskalender nutzen.</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <SubTitle id="rollen-berechtigungen">Berechtigungsmatrix</SubTitle>
        <Paragraph>
          Die folgende Tabelle zeigt die wichtigsten Berechtigungen je Rolle im Überblick (Auslieferungszustand):
        </Paragraph>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Bereich</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Admin</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Management</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Pentester</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Reviewer</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Buchhaltung</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { area: 'Projekte', admin: 'CRUD', mgmt: 'CRUD', pent: 'Lesen', rev: 'Lesen', buch: 'Lesen' },
                { area: 'Audits', admin: 'CRUD', mgmt: 'Anlegen/Bearbeiten', pent: 'Lesen', rev: 'Lesen', buch: '—' },
                { area: 'Schwachstellen', admin: 'CRUD + Review', mgmt: 'Lesen', pent: 'Erfassen + Einreichen', rev: 'Lesen + Review', buch: '—' },
                { area: 'Kunden', admin: 'CRUD', mgmt: 'CRUD', pent: '—', rev: '—', buch: 'Lesen' },
                { area: 'Angebote', admin: 'CRUD + Status', mgmt: 'CRUD + Status', pent: '—', rev: '—', buch: 'Lesen' },
                { area: 'Mitarbeiter', admin: 'CRUD', mgmt: 'Lesen', pent: '—', rev: '—', buch: '—' },
                { area: 'Rechnungen', admin: 'CRUD + Status', mgmt: 'Lesen', pent: '—', rev: '—', buch: 'CRUD + Status' },
                { area: 'Zeiterfassung', admin: 'Alle', mgmt: 'Alle einsehen, eigene bearbeiten', pent: 'Eigene', rev: 'Eigene', buch: 'Alle einsehen, eigene bearbeiten' },
                { area: 'Eingangsbelege', admin: 'Alle', mgmt: 'Eigene', pent: 'Eigene', rev: 'Eigene', buch: 'Alle einsehen und verbuchen' },
                { area: 'Urlaub genehmigen', admin: true, mgmt: true, pent: '—', rev: '—', buch: '—' },
                { area: 'Workation genehmigen', admin: true, mgmt: true, pent: '—', rev: '—', buch: '—' },
                { area: 'Krankmeldungen aller', admin: true, mgmt: true, pent: '—', rev: '—', buch: '—' },
                { area: 'Anwesenheitskalender', admin: true, mgmt: true, pent: true, rev: true, buch: true },
                { area: 'Berichte generieren', admin: true, mgmt: true, pent: true, rev: true, buch: '—' },
                { area: 'Workbench / Toolbox', admin: true, mgmt: true, pent: true, rev: true, buch: '—' },
                { area: 'Diskussion', admin: 'Moderieren', mgmt: 'Moderieren', pent: 'Schreiben', rev: 'Schreiben', buch: 'Schreiben' },
                { area: 'Datenimport/-export', admin: 'Import + Export', mgmt: 'Import + Export', pent: 'Nur Export', rev: '—', buch: '—' },
                { area: 'Kundenuploads', admin: 'Verwalten', mgmt: 'Verwalten', pent: 'Verwalten', rev: '—', buch: '—' },
                { area: 'Stammdaten', admin: true, mgmt: true, pent: '—', rev: '—', buch: '—' },
                { area: 'Administration', admin: true, mgmt: '—', pent: '—', rev: '—', buch: '—' },
              ].map((row, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ fontWeight: 500 }}>{row.area}</TableCell>
                  {[row.admin, row.mgmt, row.pent, row.rev, row.buch].map((val, j) => (
                    <TableCell key={j} sx={{ textAlign: 'center', color: val === '—' ? 'text.disabled' : 'text.primary' }}>
                      {val === true ? '✓' : val === false ? '—' : val}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Paragraph>
          „CRUD" steht für Anlegen, Lesen, Bearbeiten und Löschen. Bei <strong>Audits</strong> umfasst Management Anlegen und
          Bearbeiten (kein Löschen) und sieht alle Audits; Pentester und Reviewer sehen die ihnen zugewiesenen Audits.
          Darüber hinaus existieren weitere feingranulare Rechte, die in der{' '}
          <DocLink to="berechtigungsmatrix">Berechtigungsmatrix</DocLink> pro Rolle gesetzt werden.
        </Paragraph>

        {/* Footer */}
        <Box sx={{ mt: 8, pt: 3, borderTop: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
          <Typography variant="body2" color="text.disabled">
            STARK-AIO Dokumentation – Version 2.0
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default DocsPage;
